import { useState, useEffect, useRef } from 'react';
import api, { WEEKDAY_LABELS, formatDateShort, API } from '../lib/api';
import { toast } from 'sonner';
import { Plus, X, Save, Trash2, Download, Cloud, Phone, Copy, RefreshCw, Upload, Users, Info } from 'lucide-react';
import TageskarteTab from './AdminTageskarteTab';

export default function AdminSettings() {
  const [tab, setTab] = useState('general');

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-settings-page">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Konfiguration</p>
        <h1 className="font-display text-3xl text-[#1C1C19]">Einstellungen</h1>
      </header>

      <div className="border-b border-[#E5E5E0] mb-6 flex flex-wrap gap-1">
        {[
          { k: 'general', l: 'Allgemein' },
          { k: 'hours', l: 'Öffnungszeiten' },
          { k: 'special', l: 'Sondertage' },
          { k: 'blocks', l: 'Sperrzeiten' },
          { k: 'duration', l: 'Aufenthaltsdauer' },
          { k: 'offers', l: 'Angebote' },
          { k: 'email', l: 'E-Mail-Vorlage' },
          { k: 'feedback', l: 'Feedback' },
          { k: 'smart', l: 'Smart-Tisch' },
          { k: 'backup', l: 'Datensicherung' },
          { k: 'widget', l: 'Widget' },
          { k: 'callmonitor', l: 'Anruferkennung' },
          { k: 'tageskarte', l: 'Tageskarte' },
        ].map(t => (
          <button key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`settings-tab-${t.k}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === t.k ? 'border-[#1C1C19] text-[#1C1C19] font-medium' : 'border-transparent text-[#5C5C59] hover:text-[#1C1C19]'}`}
          >{t.l}</button>
        ))}
      </div>

      {tab === 'general' && <GeneralTab />}
      {tab === 'hours' && <OpeningHoursTab />}
      {tab === 'special' && <SpecialDaysTab />}
      {tab === 'blocks' && <BlockedPeriodsTab />}
      {tab === 'duration' && <DurationTab />}
      {tab === 'offers' && <OffersTab />}
      {tab === 'email' && <EmailTemplateTab />}
      {tab === 'feedback' && <FeedbackTab />}
      {tab === 'smart' && <SmartTableTab />}
      {tab === 'backup' && <BackupTab />}
      {tab === 'widget' && <WidgetTab />}
      {tab === 'callmonitor' && <CallMonitorTab />}
      {tab === 'tageskarte' && <TageskarteTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CallMonitorTab — Fritz!Box caller recognition setup. Shows the shared token,
// the incoming URL and a ready-to-run local bridge program (download).
// ---------------------------------------------------------------------------
const BRIDGE_TEMPLATE = `#!/usr/bin/env python3
"""La Contadina - Fritz!Box Anruferkennung (Bridge).

Liest den Fritz!Box-Anrufmonitor (Port 1012) und meldet eingehende Anrufe an
die Reservierungs-App. WICHTIG: Vorher einmalig an einem direkt an der Fritz!Box
angeschlossenen Telefon die Tastenfolge  #96*5*  waehlen, um den Anrufmonitor
zu aktivieren. Benoetigt Python 3 (keine Zusatzpakete).

Starten:  python3 lacontadina_fritzbox_bridge.py
"""
import socket, time, json, urllib.request

FRITZBOX_HOST = "fritz.box"   # oder die IP, z.B. "192.168.178.1"
FRITZBOX_PORT = 1012
API_URL   = "{API_URL}/call-monitor/incoming"
CALL_TOKEN = "{TOKEN}"

def report(phone, called):
    data = json.dumps({"phone": phone, "called_number": called}).encode()
    req = urllib.request.Request(
        API_URL, data=data, method="POST",
        headers={"Content-Type": "application/json", "X-Call-Token": CALL_TOKEN},
    )
    try:
        urllib.request.urlopen(req, timeout=10)
        print("Gemeldet:", phone)
    except Exception as e:
        print("Fehler beim Melden:", e)

def run():
    while True:
        try:
            print("Verbinde mit", FRITZBOX_HOST, FRITZBOX_PORT, "...")
            s = socket.create_connection((FRITZBOX_HOST, FRITZBOX_PORT), timeout=10)
            print("Verbunden. Warte auf Anrufe (Strg+C zum Beenden).")
            buf = ""
            while True:
                chunk = s.recv(4096).decode("latin-1", "ignore")
                if not chunk:
                    break
                buf += chunk
                while "\\n" in buf:
                    line, buf = buf.split("\\n", 1)
                    parts = line.strip().split(";")
                    # Format: datum;RING;connId;Anrufer;Angerufener;...
                    if len(parts) > 4 and parts[1] == "RING" and parts[3]:
                        report(parts[3], parts[4])
        except KeyboardInterrupt:
            print("Beendet.")
            return
        except Exception as e:
            print("Verbindung verloren:", e, "- neuer Versuch in 5s")
            time.sleep(5)

if __name__ == "__main__":
    run()
`;

// jAnrufmonitor variant: a tiny .bat that POSTs the caller number via curl.
// In jAnrufmonitor: "Erweiterung zum Starten von Anwendungen" -> select this .bat
// and set the parameter to  "%callernumber%".
const BAT_TEMPLATE = `@echo off
REM La Contadina - Anruferkennung fuer jAnrufmonitor
REM Einrichtung in jAnrufmonitor:
REM   Einstellungen -> "Erweiterung zum Starten von Anwendungen"
REM   Programm: diese .bat-Datei auswaehlen
REM   Parameter exakt eintragen:  "%callernumber%"
curl -s -X POST "{API_URL}/call-monitor/incoming" -H "X-Call-Token: {TOKEN}" -H "Content-Type: application/json" -d "{\\"phone\\":\\"%~1\\"}"
`;

function CallMonitorTab() {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const incomingUrl = `${API}/call-monitor/incoming`;

  useEffect(() => {
    api.get('/call-monitor/config')
      .then(r => setToken(r.data?.token || ''))
      .catch(() => toast.error('Konnte Token nicht laden'))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} kopiert`),
      () => toast.error('Kopieren nicht möglich'),
    );
  };

  const regenerate = async () => {
    if (!window.confirm('Neues Token erzeugen? Das alte Token wird ungültig – das Hilfsprogramm muss danach neu heruntergeladen/aktualisiert werden.')) return;
    try {
      const r = await api.post('/call-monitor/regenerate-token');
      setToken(r.data?.token || '');
      toast.success('Neues Token erzeugt');
    } catch {
      toast.error('Fehler beim Erzeugen');
    }
  };

  const downloadBridge = () => {
    const script = BRIDGE_TEMPLATE.replace('{API_URL}', API).replace('{TOKEN}', token);
    const blob = new Blob([script], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lacontadina_fritzbox_bridge.py';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadBat = () => {
    const script = BAT_TEMPLATE.replace('{API_URL}', API).replace('{TOKEN}', token);
    const blob = new Blob([script], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lacontadina_anruf_janrufmonitor.bat';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="callmonitor-tab">
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-[#1C1C19]" />
          <h2 className="font-display text-xl text-[#1C1C19]">Anruferkennung (Fritz!Box)</h2>
        </div>
        <p className="text-sm text-[#5C5C59]">
          Bei eingehenden Anrufen erscheint im Admin ein Banner mit der Rufnummer und – falls bekannt –
          dem Gast (Stammgast, Lieblingstisch). Ein Klick legt eine Reservierung mit vorausgefüllten Daten an.
          Dafür läuft ein kleines, kostenloses Hilfsprogramm bei Ihnen vor Ort im selben Netzwerk wie die Fritz!Box.
        </p>

        <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-md p-4 text-sm text-[#166534] space-y-1">
          <p className="font-medium">So richten Sie es ein (einmalig):</p>
          <ol className="list-decimal list-inside space-y-1 text-[#15803D]">
            <li>An einem direkt an der Fritz!Box angeschlossenen Telefon <b className="font-mono">#96*5*</b> wählen (aktiviert den Anrufmonitor).</li>
            <li>Hilfsprogramm unten herunterladen (Token ist bereits eingetragen).</li>
            <li>Auf einem Gerät im Restaurant-Netzwerk starten: <span className="font-mono">python3 lacontadina_fritzbox_bridge.py</span></li>
            <li>Fertig – ein Testanruf zeigt sofort den Banner im Admin.</li>
          </ol>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-medium uppercase tracking-wider text-[#5C5C59]">Verbindungsdaten (für das Hilfsprogramm)</h3>

        <div>
          <label className="text-xs text-[#5C5C59]">Melde-URL</label>
          <div className="flex items-center gap-2 mt-1">
            <input readOnly value={incomingUrl} data-testid="callmonitor-url" className="flex-1 font-mono text-xs px-3 py-2 border border-[#E5E5E0] rounded bg-[#F9F9F7]" />
            <button onClick={() => copy(incomingUrl, 'URL')} data-testid="callmonitor-copy-url" className="p-2 border border-[#E5E5E0] rounded hover:bg-[#F9F9F7]" title="Kopieren"><Copy className="w-4 h-4" /></button>
          </div>
        </div>

        <div>
          <label className="text-xs text-[#5C5C59]">Token (geheim)</label>
          <div className="flex items-center gap-2 mt-1">
            <input readOnly value={loading ? 'Lädt…' : token} data-testid="callmonitor-token" className="flex-1 font-mono text-xs px-3 py-2 border border-[#E5E5E0] rounded bg-[#F9F9F7]" />
            <button onClick={() => copy(token, 'Token')} data-testid="callmonitor-copy-token" className="p-2 border border-[#E5E5E0] rounded hover:bg-[#F9F9F7]" title="Kopieren"><Copy className="w-4 h-4" /></button>
            <button onClick={regenerate} data-testid="callmonitor-regenerate" className="p-2 border border-[#E5E5E0] rounded hover:bg-[#F9F9F7] text-[#EF4444]" title="Neues Token erzeugen"><RefreshCw className="w-4 h-4" /></button>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-1">Halten Sie das Token geheim. Bei „Neu erzeugen“ muss das Hilfsprogramm erneut heruntergeladen werden.</p>
        </div>

        <button
          onClick={downloadBridge}
          disabled={loading || !token}
          data-testid="callmonitor-download"
          className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-4 py-2.5 rounded text-sm hover:bg-[#3A3A35] transition disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Hilfsprogramm herunterladen (Token eingetragen)
        </button>

        <div className="pt-4 mt-2 border-t border-[#E5E5E0]">
          <p className="text-sm font-medium text-[#1C1C19]">Alternative: jAnrufmonitor</p>
          <p className="text-xs text-[#5C5C59] mt-1 mb-2">
            Wenn Sie bereits <b>jAnrufmonitor</b> nutzen, brauchen Sie das Python-Programm nicht.
            Laden Sie stattdessen diese <span className="font-mono">.bat</span>-Datei herunter und tragen Sie sie in jAnrufmonitor unter
            „Erweiterung zum Starten von Anwendungen" ein – als Parameter <span className="font-mono">"%callernumber%"</span>.
            (Voraussetzung: Windows 10/11 mit <span className="font-mono">curl</span>, ist normalerweise vorinstalliert.)
          </p>
          <button
            onClick={downloadBat}
            disabled={loading || !token}
            data-testid="callmonitor-download-bat"
            className="inline-flex items-center gap-2 border border-[#1C1C19] text-[#1C1C19] px-4 py-2.5 rounded text-sm hover:bg-[#F9F9F7] transition disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> jAnrufmonitor-Datei (.bat) herunterladen
          </button>
        </div>
      </div>
    </div>
  );
}

const OFFER_CATEGORIES = [
  { v: 'sekt', l: 'Sekt / Aperitif' },
  { v: 'getraenk', l: 'Getränk' },
  { v: 'vorspeise', l: 'Vorspeise' },
  { v: 'menu', l: 'Menü' },
  { v: 'dessert', l: 'Dessert' },
  { v: 'sonstiges', l: 'Sonstiges' },
];

function OffersTab() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', price: '', category: 'sekt', min_persons: 1, max_persons: '', image_url: '', per_person: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/offers/all');
      setOffers(r.data || []);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) { toast.info('Bitte einen Namen eingeben'); return; }
    try {
      await api.post('/offers', {
        name: form.name.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price) || 0,
        category: form.category,
        per_person: !!form.per_person,
        min_persons: parseInt(form.min_persons) || 1,
        max_persons: form.max_persons === '' ? null : (parseInt(form.max_persons) || null),
        image_url: form.image_url.trim(),
        active: true,
        sort_order: offers.length,
      });
      toast.success('Angebot angelegt');
      setForm({ name: '', description: '', price: '', category: 'sekt', min_persons: 1, max_persons: '', image_url: '', per_person: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Anlegen'); }
  };

  const update = async (id, patch) => {
    try { await api.put(`/offers/${id}`, patch); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Angebot wirklich löschen?')) return;
    try { await api.delete(`/offers/${id}`); toast.success('Gelöscht'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  return (
    <div className="space-y-6" data-testid="settings-offers-tab">
      <div>
        <h2 className="font-display text-xl text-[#1C1C19]">Vorbestell-Angebote</h2>
        <p className="text-sm text-[#5C5C59] mt-1 max-w-2xl">
          Sekt, Vorspeisen oder Menüs, die Gäste bei der Online-Reservierung optional vorbestellen können.
          Mit „Min./Max. Personen" steuerst du, ab/bis welcher Gruppengröße ein Angebot erscheint
          (z. B. „Gruppenmenü" erst ab 6 Personen).
        </p>
      </div>

      {/* New offer form */}
      <div className="border border-[#E5E5E0] rounded-lg p-4 bg-[#F9F9F7]">
        <p className="text-[11px] uppercase tracking-wider text-[#5C5C59] mb-3">Neues Angebot</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs text-[#5C5C59]">Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              data-testid="offer-new-name" placeholder="z. B. Prosecco zur Begrüßung"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]" />
          </div>
          <div>
            <label className="text-xs text-[#5C5C59]">Preis (€)</label>
            <input type="number" min="0" step="0.5" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
              data-testid="offer-new-price" placeholder="6,50"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]" />
            <label className="flex items-center gap-1.5 mt-1.5 text-xs text-[#5C5C59] cursor-pointer">
              <input type="checkbox" checked={!!form.per_person} onChange={e => setForm({ ...form, per_person: e.target.checked })} data-testid="offer-new-perperson" />
              Preis pro Person (× Personenzahl)
            </label>
          </div>
          <div>
            <label className="text-xs text-[#5C5C59]">Kategorie</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
              data-testid="offer-new-category"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1C1C19]">
              {OFFER_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#5C5C59]">Min. Personen</label>
            <input type="number" min="1" value={form.min_persons} onChange={e => setForm({ ...form, min_persons: e.target.value })}
              data-testid="offer-new-min" className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]" />
          </div>
          <div>
            <label className="text-xs text-[#5C5C59]">Max. Personen (leer = unbegrenzt)</label>
            <input type="number" min="1" value={form.max_persons} onChange={e => setForm({ ...form, max_persons: e.target.value })}
              data-testid="offer-new-max" placeholder="—" className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]" />
          </div>
          <div className="lg:col-span-3">
            <label className="text-xs text-[#5C5C59]">Beschreibung (optional)</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              data-testid="offer-new-desc" placeholder="kurze Beschreibung für den Gast"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]" />
          </div>
        </div>
        <div className="mt-3">
          <button onClick={add} data-testid="offer-add-btn"
            className="px-4 py-2 rounded text-sm bg-[#1C1C19] hover:bg-[#3A3A35] text-white flex items-center gap-1.5 transition">
            <Plus className="w-4 h-4" /> Anlegen
          </button>
        </div>
      </div>

      {/* Existing offers */}
      <div className="space-y-2">
        {loading && <p className="text-sm text-[#9CA3AF]">Lädt…</p>}
        {!loading && offers.length === 0 && (
          <p className="text-sm text-[#9CA3AF] italic">Noch keine Angebote angelegt.</p>
        )}
        {offers.map(o => (
          <OfferRow key={o.id} offer={o} onUpdate={update} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}

function OfferRow({ offer, onUpdate, onDelete }) {
  const [name, setName] = useState(offer.name);
  const [price, setPrice] = useState(offer.price);
  const [category, setCategory] = useState(offer.category);
  const [minP, setMinP] = useState(offer.min_persons);
  const [maxP, setMaxP] = useState(offer.max_persons ?? '');
  const catLabel = OFFER_CATEGORIES.find(c => c.v === offer.category)?.l || offer.category;

  return (
    <div className={`border rounded-lg p-3 ${offer.active ? 'border-[#E5E5E0] bg-white' : 'border-dashed border-[#E5E5E0] bg-[#F9F9F7] opacity-70'}`}
      data-testid={`offer-row-${offer.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
        <div className="sm:col-span-4">
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{catLabel}</label>
          <input value={name} onChange={e => setName(e.target.value)}
            onBlur={() => { if (name.trim() && name !== offer.name) onUpdate(offer.id, { name: name.trim() }); }}
            data-testid={`offer-name-${offer.id}`}
            className="w-full px-2 py-1.5 border border-transparent hover:border-[#E5E5E0] focus:border-[#1C1C19] rounded text-sm font-medium focus:outline-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Preis €</label>
          <input type="number" min="0" step="0.5" value={price} onChange={e => setPrice(e.target.value)}
            onBlur={() => { const p = parseFloat(price) || 0; if (p !== offer.price) onUpdate(offer.id, { price: p }); }}
            data-testid={`offer-price-${offer.id}`}
            className="w-full px-2 py-1.5 border border-[#E5E5E0] focus:border-[#1C1C19] rounded text-sm focus:outline-none" />
          <label className="flex items-center gap-1 mt-1 text-[10px] text-[#5C5C59] cursor-pointer">
            <input type="checkbox" checked={!!offer.per_person} onChange={e => onUpdate(offer.id, { per_person: e.target.checked })} data-testid={`offer-perperson-${offer.id}`} />
            pro Person
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Kategorie</label>
          <select value={category} onChange={e => { setCategory(e.target.value); onUpdate(offer.id, { category: e.target.value }); }}
            data-testid={`offer-category-${offer.id}`}
            className="w-full px-2 py-1.5 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1C1C19]">
            {OFFER_CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
        <div className="sm:col-span-1">
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Min</label>
          <input type="number" min="1" value={minP} onChange={e => setMinP(e.target.value)}
            onBlur={() => { const v = parseInt(minP) || 1; if (v !== offer.min_persons) onUpdate(offer.id, { min_persons: v }); }}
            data-testid={`offer-min-${offer.id}`}
            className="w-full px-2 py-1.5 border border-[#E5E5E0] focus:border-[#1C1C19] rounded text-sm focus:outline-none" />
        </div>
        <div className="sm:col-span-1">
          <label className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Max</label>
          <input type="number" min="1" value={maxP} onChange={e => setMaxP(e.target.value)}
            onBlur={() => { const v = maxP === '' ? null : (parseInt(maxP) || null); if (v !== (offer.max_persons ?? null)) onUpdate(offer.id, { max_persons: v }); }}
            data-testid={`offer-max-${offer.id}`} placeholder="—"
            className="w-full px-2 py-1.5 border border-[#E5E5E0] focus:border-[#1C1C19] rounded text-sm focus:outline-none" />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2 justify-end">
          <button onClick={() => onUpdate(offer.id, { active: !offer.active })}
            data-testid={`offer-toggle-${offer.id}`}
            className={`px-2 py-1.5 rounded text-xs border transition ${offer.active ? 'border-[#22C55E] text-[#22C55E]' : 'border-[#9CA3AF] text-[#9CA3AF]'}`}>
            {offer.active ? 'Aktiv' : 'Inaktiv'}
          </button>
          <button onClick={() => onDelete(offer.id)} data-testid={`offer-delete-${offer.id}`}
            className="p-1.5 text-[#9CA3AF] hover:text-[#EF4444] transition" title="Löschen">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {offer.description && <p className="text-xs text-[#5C5C59] mt-1.5">{offer.description}</p>}
    </div>
  );
}

function BackupTab() {
  const [busy, setBusy] = useState(false);
  const [driveBusy, setDriveBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const downloadGuestTemplate = async () => {
    try {
      const resp = await api.get('/guests/import-template', { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gaeste_vorlage.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Vorlage konnte nicht geladen werden');
    }
  };

  const handleGuestImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/guests/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(r.data);
      toast.success(`${r.data.total} Gäste importiert (${r.data.imported} neu, ${r.data.updated} aktualisiert)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImportBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const loadStatus = async () => {
    try {
      const r = await api.get('/backup/drive/status');
      setStatus(r.data);
      setEmailInput(r.data?.backup_email || '');
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadStatus();
    // Handle OAuth redirect feedback
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('drive_connected')) {
      toast.success('Google Drive verbunden');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (sp.get('drive_error')) {
      toast.error('Drive-Verbindung fehlgeschlagen: ' + sp.get('drive_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const startOAuth = async () => {
    try {
      const r = await api.get('/backup/drive/oauth/start');
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'OAuth-Start fehlgeschlagen');
    }
  };

  const disconnectOAuth = async () => {
    if (!window.confirm('Google-Drive-Verbindung wirklich trennen? Auto-Backups in Drive werden gestoppt.')) return;
    try {
      await api.post('/backup/drive/oauth/disconnect');
      toast.success('Verbindung getrennt');
      await loadStatus();
    } catch (e) {
      toast.error('Fehler beim Trennen');
    }
  };

  const download = async (onlyFuture) => {
    setBusy(true);
    try {
      const resp = await api.get(`/backup/export?only_future=${onlyFuture}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-T:]/g, '');
      a.href = url;
      a.download = `reservations_${onlyFuture ? 'future' : 'all'}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Backup heruntergeladen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setDriveBusy(true);
    setTestResult(null);
    try {
      const r = await api.post('/backup/drive/test');
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Verbunden mit „${r.data.folder_name || 'Ordner'}"`);
      else toast.error(r.data.message || 'Verbindung fehlgeschlagen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test fehlgeschlagen');
    } finally {
      setDriveBusy(false);
    }
  };

  const runNow = async () => {
    setDriveBusy(true);
    try {
      const r = await api.post('/backup/drive/run-now');
      if (r.data.ok) toast.success('Backup zu Google Drive hochgeladen');
      else toast.error(r.data.error || 'Upload fehlgeschlagen');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setDriveBusy(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  const saveEmail = async () => {
    setEmailSaving(true);
    try {
      await api.put('/settings', { backup_email: emailInput.trim() });
      toast.success('Backup-E-Mail gespeichert');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="backup-tab">
      {/* Manual Excel Download */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6">
        <h2 className="font-display text-lg mb-1">Excel-Backup (manuell)</h2>
        <p className="text-xs text-[#5C5C59] mb-5">Sicherung als XLSX-Datei herunterladen.</p>
        <div className="space-y-3">
          <button onClick={() => download(true)} disabled={busy} data-testid="backup-download-future" className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-[#1C1C19] rounded text-sm hover:bg-[#1C1C19] hover:text-white transition disabled:opacity-50">
            <span className="flex items-center gap-3">
              <Download className="w-4 h-4" />
              <span>
                <span className="font-medium block">Zukünftige Reservierungen</span>
                <span className="text-[10px] opacity-70">Empfohlen — kompakt</span>
              </span>
            </span>
            <span className="text-xs">.xlsx</span>
          </button>
          <button onClick={() => download(false)} disabled={busy} data-testid="backup-download-all" className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] transition disabled:opacity-50">
            <span className="flex items-center gap-3">
              <Download className="w-4 h-4 text-[#5C5C59]" />
              <span>
                <span className="font-medium block">Komplettsicherung (alle Daten)</span>
                <span className="text-[10px] text-[#5C5C59]">Inklusive vergangener Reservierungen</span>
              </span>
            </span>
            <span className="text-xs text-[#5C5C59]">.xlsx</span>
          </button>
        </div>
      </div>

      {/* Gäste-Import (Excel/CSV) */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="guest-import-card">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-[#1C1C19]" />
          <h2 className="font-display text-lg">Gäste importieren (Excel/CSV)</h2>
        </div>
        <p className="text-xs text-[#5C5C59] mb-5">
          Stammgäste hochladen — sie werden bei Anruf/Telefon-Eingabe sofort erkannt (Name, E-Mail, optional Lieblingstisch),
          auch ohne vorherige Reservierung. Spalten: <b>Name, Telefon, E-Mail, Lieblingstisch</b> (Tischnummer).
        </p>
        <div className="space-y-3">
          <button
            onClick={downloadGuestTemplate}
            data-testid="guest-import-template"
            className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] transition"
          >
            <span className="flex items-center gap-3">
              <Download className="w-4 h-4 text-[#5C5C59]" />
              <span className="font-medium">Excel-Vorlage herunterladen</span>
            </span>
            <span className="text-xs text-[#5C5C59]">.xlsx</span>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.csv"
            onChange={handleGuestImport}
            className="hidden"
            data-testid="guest-import-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importBusy}
            data-testid="guest-import-upload"
            className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-[#1C1C19] rounded text-sm hover:bg-[#1C1C19] hover:text-white transition disabled:opacity-50"
          >
            <span className="flex items-center gap-3">
              <Upload className="w-4 h-4" />
              <span className="font-medium">{importBusy ? 'Importiere…' : 'Datei hochladen & importieren'}</span>
            </span>
            <span className="text-xs">.xlsx / .csv</span>
          </button>
        </div>

        {importResult && (
          <div className="mt-4 bg-[#F0FDF4] border border-[#86EFAC] rounded-md p-3 text-sm text-[#166534]" data-testid="guest-import-result">
            <p className="font-medium">{importResult.total} Gäste importiert</p>
            <p className="text-xs text-[#15803D]">{importResult.imported} neu · {importResult.updated} aktualisiert{importResult.skipped ? ` · ${importResult.skipped} übersprungen (keine Telefonnummer)` : ''}</p>
            {importResult.errors?.length > 0 && (
              <ul className="mt-2 text-xs text-[#B45309] list-disc list-inside space-y-0.5">
                {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="auto-backup-toggle-section">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-medium text-sm text-[#1C1C19]">Automatische Backups aktivieren</h3>
            <p className="text-xs text-[#5C5C59] mt-0.5">
              Schaltet den Scheduler komplett aus (E-Mail-Versand + Drive-Upload). Manuelles Herunterladen bleibt möglich.
            </p>
            <p className={`text-[10px] mt-1 ${status?.backup_auto_enabled ? 'text-[#16A34A]' : 'text-[#EF4444]'}`}>
              Status: {status?.backup_auto_enabled ? 'AKTIV — läuft alle 6 h + täglich 02:00' : 'DEAKTIVIERT — Scheduler pausiert'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!status?.backup_auto_enabled}
            disabled={emailSaving}
            onClick={async () => {
              const next = !status?.backup_auto_enabled;
              setEmailSaving(true);
              try {
                await api.put('/settings', { backup_auto_enabled: next });
                toast.success(next ? 'Auto-Backup aktiviert' : 'Auto-Backup deaktiviert');
                await loadStatus();
              } catch (e) {
                toast.error('Fehler beim Speichern');
              } finally {
                setEmailSaving(false);
              }
            }}
            data-testid="auto-backup-toggle"
            className={`relative w-12 h-7 rounded-full transition shrink-0 ${status?.backup_auto_enabled ? 'bg-[#16A34A]' : 'bg-[#9CA3AF]'} disabled:opacity-50`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${status?.backup_auto_enabled ? 'translate-x-5' : 'translate-x-0'}`}
            ></span>
          </button>
        </div>
      </div>

      {/* E-Mail Auto-Backup */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="email-backup-section">
        <h2 className="font-display text-lg mb-1">E-Mail Auto-Backup</h2>
        <p className="text-xs text-[#5C5C59] mb-4">
          Das System schickt die Excel-Sicherung automatisch alle 6 h sowie täglich um 02:00 (Berlin) an die hier hinterlegte E-Mail.
        </p>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Empfänger-E-Mail</span>
          <div className="flex gap-2">
            <input
              type="email"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="z.B. contadinadresden@gmail.com"
              data-testid="backup-email-input"
              className="flex-1 px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono"
            />
            <button
              onClick={saveEmail}
              disabled={emailSaving}
              data-testid="backup-email-save"
              className="px-4 py-2 bg-[#1C1C19] text-white rounded text-sm hover:bg-[#3C3C39] transition disabled:opacity-50"
            >
              <Save className="w-4 h-4 inline-block mr-1" /> Speichern
            </button>
          </div>
        </label>
        {status?.backup_email && (
          <p className="text-[10px] text-[#5C5C59] mt-2">
            Aktiv → Backups gehen an <b className="font-mono">{status.backup_email}</b>. Zum Deaktivieren Feld leeren und speichern.
          </p>
        )}
      </div>

      {/* Google Drive Auto Backup */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="drive-section">
        <div className="flex items-start gap-3 mb-4">
          <Cloud className="w-6 h-6 text-[#1C1C19]" />
          <div className="flex-1">
            <h2 className="font-display text-lg">Google Drive Auto-Backup</h2>
            <p className="text-xs text-[#5C5C59]">Automatische Sicherung alle 6 h + täglich um 02:00 (Berlin-Zeit).</p>
          </div>
          <span className={`px-2 py-0.5 text-[10px] rounded uppercase tracking-wider ${status?.oauth_connected ? 'bg-[#22C55E]/10 text-[#16A34A]' : status?.configured ? 'bg-[#FBBF24]/10 text-[#92400E]' : 'bg-[#9CA3AF]/10 text-[#5C5C59]'}`}>
            {status?.oauth_connected ? 'OAuth verbunden' : status?.auth_mode === 'service_account' ? 'Service Account' : 'Nicht verbunden'}
          </span>
        </div>

        {/* OAuth Connect / Disconnect */}
        {status?.oauth_configured && (
          <div className="mb-4 p-4 bg-[#F9F9F7] rounded border border-[#E5E5E0]">
            {status?.oauth_connected ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs">
                  <p className="text-[#5C5C59] mb-0.5">Verbunden als:</p>
                  <p className="font-mono text-[#1C1C19]" data-testid="drive-oauth-email">{status.oauth_account_email}</p>
                </div>
                <button
                  onClick={disconnectOAuth}
                  data-testid="drive-oauth-disconnect"
                  className="px-3 py-1.5 text-xs border border-[#EF4444] text-[#EF4444] rounded hover:bg-[#EF4444] hover:text-white transition"
                >Verbindung trennen</button>
              </div>
            ) : (
              <div>
                <p className="text-xs text-[#5C5C59] mb-3">
                  Verbinden Sie ein <b>privates Google-Konto</b> per einmaligem Login. Dateien werden in <b>Ihre eigene Drive-Ablage</b> hochgeladen — keine Quota-Probleme.
                </p>
                <button
                  onClick={startOAuth}
                  data-testid="drive-oauth-connect"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] transition shadow-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"/></svg>
                  Mit Google verbinden
                </button>
              </div>
            )}
          </div>
        )}

        {status?.configured && (
          <>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mb-4 p-3 bg-[#F9F9F7] rounded">
              <dt className="text-[#5C5C59]">Service-Konto</dt>
              <dd className="font-mono text-[10px] break-all" data-testid="drive-email">{status.service_account_email}</dd>
              <dt className="text-[#5C5C59]">Ziel-Ordner-ID</dt>
              <dd className="font-mono text-[10px] break-all">{status.folder_id}</dd>
              <dt className="text-[#5C5C59]">Letzte Sicherung</dt>
              <dd className="font-medium" data-testid="drive-last-run">
                {status.last_status === 'success' && <span className="text-[#16A34A]">✓ </span>}
                {status.last_status === 'failed' && <span className="text-[#EF4444]">✗ </span>}
                {fmt(status.last_run_at)}
                {status.last_trigger && <span className="text-[#9CA3AF] ml-1">({status.last_trigger})</span>}
              </dd>
              {status.last_filename && (<>
                <dt className="text-[#5C5C59]">Datei</dt>
                <dd className="font-mono text-[10px] truncate">{status.last_filename}</dd>
              </>)}
              {status.last_file_url && (<>
                <dt className="text-[#5C5C59]">Link</dt>
                <dd><a href={status.last_file_url} target="_blank" rel="noreferrer" className="text-[#1C1C19] underline text-[10px]">Datei in Drive öffnen ↗</a></dd>
              </>)}
              {status.last_error && (<>
                <dt className="text-[#5C5C59]">Letzter Fehler</dt>
                <dd className="text-[#EF4444] text-[10px] break-words">{status.last_error}</dd>
              </>)}
            </dl>

            <div className="flex gap-2">
              <button onClick={testConnection} disabled={driveBusy} data-testid="drive-test-btn" className="px-3 py-1.5 text-xs border border-[#E5E5E0] rounded hover:border-[#1C1C19] transition disabled:opacity-50">
                Verbindung testen
              </button>
              <button onClick={runNow} disabled={driveBusy} data-testid="drive-run-now-btn" className="px-3 py-1.5 text-xs border border-[#1C1C19] bg-[#1C1C19] text-white rounded hover:bg-[#3C3C39] transition disabled:opacity-50">
                Jetzt sichern
              </button>
            </div>

            {testResult && (
              <div className={`mt-3 p-3 rounded text-xs ${testResult.ok ? 'bg-[#22C55E]/10 text-[#16A34A]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-testid="drive-test-result">
                {testResult.ok ? '✓' : '✗'} {testResult.message}
                {testResult.folder_name && <> · Ordner: <b>{testResult.folder_name}</b></>}
              </div>
            )}

            {!status.oauth_connected && !testResult?.ok && status.auth_mode === 'service_account' && (
              <div className="mt-3 p-3 bg-[#FEF3C7] border border-[#FBBF24] rounded text-xs text-[#92400E]" data-testid="drive-share-hint">
                <p className="font-medium mb-1">⚠️ Service-Account hat kein Drive-Kontingent</p>
                <p>Empfehlung: oben den Button <b>„Mit Google verbinden"</b> nutzen (OAuth) — das funktioniert mit jedem Google-Konto und ohne Workspace.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AiAssistantSettings({ s, setS }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/ai-assistant/stats').then(r => setStats(r.data)).catch(() => {}); }, []);
  const enabled = !!s.ai_assistant_enabled;
  const budget = s.ai_assistant_monthly_budget_eur ?? 10;
  const spent = stats?.spent_eur ?? 0;
  const pct = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
  return (
    <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
      <h2 className="font-display text-lg mb-2">KI-Gastgeber (Chat-Assistent)</h2>
      <p className="text-sm text-[#5C5C59] mb-3">
        Ein freundlicher Chat-Assistent auf der Buchungsseite beantwortet Gästen Fragen zu Öffnungszeiten,
        geschlossenen Tagen und Reservierungen – auf Italienisch, Deutsch und Englisch. Er legt KEINE
        Reservierungen an und verweist dafür auf das Formular. Läuft mit Gemini 3 Flash; die Kosten werden
        pro Monat mitgezählt und bei Erreichen des Budgets wird der Assistent automatisch pausiert.
      </p>
      <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => setS({ ...s, ai_assistant_enabled: e.target.checked })}
          data-testid="g-ai-enabled"
          className="w-4 h-4 accent-[#1C1C19]"
        />
        Chat-Assistent anzeigen
      </label>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput
          label="Monatsbudget (€)"
          type="number"
          value={budget}
          onChange={v => { const n = parseFloat(v); setS({ ...s, ai_assistant_monthly_budget_eur: isNaN(n) ? 0 : Math.max(0, n) }); }}
          testId="g-ai-budget"
          hint="Bei Erreichen pausiert der Assistent bis zum Monatswechsel. 0 = kein Limit."
        />
        <div className="p-4 bg-[#F9F9F7] rounded" data-testid="g-ai-usage">
          <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-1">Verbrauch diesen Monat</p>
          <p className="text-2xl font-display text-[#1C1C19]">
            {spent.toFixed(2).replace('.', ',')} € <span className="text-sm text-[#5C5C59]">/ {Number(budget).toFixed(2).replace('.', ',')} €</span>
          </p>
          <div className="h-2 bg-[#E5E5E0] rounded-full mt-2 overflow-hidden">
            <div className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-[#1C1C19]'}`} style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-[#9CA3AF] mt-2">
            {stats?.messages ?? 0} Antworten · {stats?.conversations ?? 0} Gespräche
            {stats?.budget_exceeded ? ' · aktuell pausiert (Budget erreicht)' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

function GeneralTab() {
  const [s, setS] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => { api.get('/settings').then(r => setS(r.data)); }, []);
  if (!s) return <div>Lädt...</div>;

  const save = async () => {
    try {
      await api.put('/settings', s);
      toast.success('Gespeichert');
    } catch { toast.error('Fehler'); }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    setEmailResult(null);
    try {
      const r = await api.post('/settings/test-email');
      setEmailResult(r.data);
      if (r.data.ok) toast.success('Test-E-Mail gesendet');
      else toast.warning(r.data.reason || 'Versand übersprungen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setTestingEmail(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4" data-testid="settings-general">
      <h2 className="font-display text-lg mb-2">Restaurant</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput label="Name" value={s.restaurant_name} onChange={v => setS({...s, restaurant_name: v})} testId="g-name" />
        <FieldInput label="Telefon" value={s.restaurant_phone} onChange={v => setS({...s, restaurant_phone: v})} testId="g-phone" />
        <FieldInput label="E-Mail" value={s.restaurant_email} onChange={v => setS({...s, restaurant_email: v})} testId="g-email" />
        <FieldInput label="Adresse" value={s.restaurant_address} onChange={v => setS({...s, restaurant_address: v})} testId="g-address" />
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Branding</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput label="Logo-Text (Wordmark)" value={s.logo_text} onChange={v => setS({...s, logo_text: v})} testId="g-logo-text" />
      </div>
      <div className="mt-4">
        <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Logo-Bild (optional)</label>
        <div className="flex gap-2 items-start flex-wrap">
          {s.logo_url && (
            <img src={s.logo_url} alt="Logo" className="h-16 w-auto object-contain rounded border border-[#E5E5E0] bg-white p-2" data-testid="g-logo-preview" />
          )}
          <div className="flex-1 min-w-[240px]">
            <input type="text" value={s.logo_url || ''} onChange={e => setS({...s, logo_url: e.target.value})} data-testid="g-logo-url" placeholder="https://... oder data:image/..." className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
            <div className="flex gap-2 mt-2">
              <label className="px-3 py-1.5 border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white rounded text-xs cursor-pointer transition" data-testid="g-logo-upload">
                Logo hochladen
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 1_000_000) { toast.error('Maximal 1 MB erlaubt'); return; }
                  const reader = new FileReader();
                  reader.onload = (ev) => setS({...s, logo_url: ev.target.result});
                  reader.readAsDataURL(file);
                }} />
              </label>
              {s.logo_url && (
                <button type="button" onClick={() => setS({...s, logo_url: ''})} data-testid="g-logo-clear" className="px-3 py-1.5 border border-[#E5E5E0] text-[#5C5C59] hover:border-[#EF4444] hover:text-[#EF4444] rounded text-xs">Entfernen</button>
              )}
            </div>
            <p className="text-[10px] text-[#5C5C59] mt-1">Max. 1 MB (PNG/JPG/SVG). Wird auf allen Kunden-Seiten als Logo angezeigt. Wenn leer, wird der Logo-Text in der Playball-Schrift verwendet.</p>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Booking-Hero-Bild</label>
        <div className="flex gap-2 items-start flex-wrap">
          {s.booking_image_url && (
            <img src={s.booking_image_url} alt="Booking-Hero" className="w-40 h-24 object-cover rounded border border-[#E5E5E0]" data-testid="g-booking-preview" />
          )}
          <div className="flex-1 min-w-[240px]">
            <input type="text" value={s.booking_image_url || ''} onChange={e => setS({...s, booking_image_url: e.target.value})} data-testid="g-booking-url" placeholder="https://... oder data:image/..." className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
            <div className="flex gap-2 mt-2">
              <label className="px-3 py-1.5 border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white rounded text-xs cursor-pointer transition" data-testid="g-booking-upload">
                Bild hochladen
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2_000_000) { toast.error('Maximal 2 MB erlaubt'); return; }
                  const reader = new FileReader();
                  reader.onload = (ev) => setS({...s, booking_image_url: ev.target.result});
                  reader.readAsDataURL(file);
                }} />
              </label>
              {s.booking_image_url && (
                <button type="button" onClick={() => setS({...s, booking_image_url: ''})} data-testid="g-booking-clear" className="px-3 py-1.5 border border-[#E5E5E0] text-[#5C5C59] hover:border-[#EF4444] hover:text-[#EF4444] rounded text-xs">Entfernen</button>
              )}
            </div>
            <p className="text-[10px] text-[#5C5C59] mt-1">Max. 2 MB. Wird auf der Kunden-Buchungsseite als Hero-Bild verwendet.</p>
          </div>
        </div>
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Reservierungslogik</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <FieldInput label="Zeit-Intervall (Min.)" type="number" value={s.reservation_interval} onChange={v => setS({...s, reservation_interval: parseInt(v) || 15})} testId="g-interval" />
        <FieldInput label="Standard-Aufenthalt (Min.)" type="number" value={s.default_duration_minutes} onChange={v => setS({...s, default_duration_minutes: parseInt(v) || 120})} testId="g-default-dur" />
        <FieldInput label="Walk-in Standard-Aufenthalt (Min.) — 0 = wie oben" type="number" value={s.walkin_default_duration_minutes ?? 0} onChange={v => setS({...s, walkin_default_duration_minutes: parseInt(v) || 0})} testId="g-walkin-dur" />
        <FieldInput label="Mindest-Aufenthalt Kurzslot (Min.)" type="number" value={s.min_gap_duration_minutes} onChange={v => setS({...s, min_gap_duration_minutes: parseInt(v) || 90})} testId="g-min-gap" />
        <FieldInput
          label="Mindest-Aufenthalt Kurzslot Tischgruppe (Min.)"
          type="number"
          value={s.min_gap_combination_minutes ?? 0}
          onChange={v => setS({...s, min_gap_combination_minutes: parseInt(v) || 0})}
          testId="g-min-gap-combo"
          hint="Separat einstellbar für Tischgruppen. 0 = denselben Wert wie Einzeltische verwenden."
        />
        <FieldInput label="Pufferzeit zwischen Reservierungen (Min.)" hint="0 = kein Puffer zwischen zwei Reservierungen." type="number" value={s.buffer_minutes} onChange={v => { const n = parseInt(v); setS({...s, buffer_minutes: isNaN(n) ? 0 : Math.max(0, n)}); }} testId="g-buffer" />
        <FieldInput label="Max. Personen Online" type="number" value={s.max_online_persons} onChange={v => setS({...s, max_online_persons: parseInt(v) || 12})} testId="g-max-online" />
        <FieldInput label="Mindestdauer letzter Platz (Min.)" type="number" value={s.last_seating_min_minutes ?? 60} onChange={v => { const n = parseInt(v); setS({...s, last_seating_min_minutes: isNaN(n) ? 0 : Math.max(0, n)}); }} testId="g-last-seating-min" hint="Kürzere Buchungen für die letzten Startzeiten vor Blockende (z. B. Mittag bis 14:00), damit späte Zeiten wie 12:45/13:00 buchbar bleiben. 0 = aus." />
      </div>

      <div className="mt-4 border border-[#E5E5E0] rounded-lg p-4 bg-[#FAFAF8]">
        <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Max. Personen Online pro Wochentag (optional)</label>
        <p className="text-xs text-[#5C5C59] mb-3">Pro Wochentag abweichend begrenzbar. Leer lassen = Standardwert oben ({s.max_online_persons || 12}) verwenden. Beispiel: Mo 15, Fr & Sa 10.</p>
        <div className="grid grid-cols-7 gap-2">
          {[['0','Mo'],['1','Di'],['2','Mi'],['3','Do'],['4','Fr'],['5','Sa'],['6','So']].map(([k, label]) => {
            const byWd = s.max_online_persons_by_weekday || {};
            return (
              <div key={k} className="flex flex-col items-center">
                <span className="text-xs text-[#5C5C59] mb-1">{label}</span>
                <input
                  type="number" min="0"
                  value={byWd[k] ?? ''}
                  placeholder={String(s.max_online_persons || 12)}
                  data-testid={`g-max-online-wd-${k}`}
                  onChange={e => {
                    const next = { ...(s.max_online_persons_by_weekday || {}) };
                    const n = parseInt(e.target.value);
                    if (!e.target.value || isNaN(n) || n <= 0) delete next[k];
                    else next[k] = n;
                    setS({ ...s, max_online_persons_by_weekday: next });
                  }}
                  className="w-full px-2 py-1.5 border border-[#E5E5E0] rounded text-sm text-center"
                />
              </div>
            );
          })}
        </div>
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Online-Buchungsfenster</h2>
      <p className="text-xs text-[#5C5C59] mb-3">Begrenzt, wie weit im Voraus und wie kurzfristig Gäste online reservieren können. Gilt nur für die Kundenseite — im Admin sind weiterhin alle Termine buchbar.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Reservierbar bis maximal</label>
          <select
            value={s.booking_window_months ?? 0}
            onChange={v => setS({ ...s, booking_window_months: parseInt(v.target.value) })}
            data-testid="g-booking-window"
            className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white"
          >
            <option value={0}>Kein Limit</option>
            <option value={1}>1 Monat im Voraus</option>
            <option value={2}>2 Monate im Voraus</option>
            <option value={3}>3 Monate im Voraus</option>
            <option value={6}>6 Monate im Voraus</option>
            <option value={12}>12 Monate im Voraus</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Mindest-Vorlaufzeit</label>
          <select
            value={s.min_lead_minutes ?? 0}
            onChange={v => setS({ ...s, min_lead_minutes: parseInt(v.target.value) })}
            data-testid="g-min-lead"
            className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white"
          >
            <option value={0}>Keine (bis zur letzten Minute)</option>
            <option value={30}>30 Minuten vorher</option>
            <option value={60}>1 Stunde vorher</option>
            <option value={120}>2 Stunden vorher</option>
            <option value={180}>3 Stunden vorher</option>
            <option value={240}>4 Stunden vorher</option>
            <option value={1440}>1 Tag vorher</option>
          </select>
        </div>
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Info-Tafel (Urlaub / geschlossene Tage)</h2>
      <p className="text-xs text-[#5C5C59] mb-3">Zeigt Gästen ein auffälliges Hinweis-Banner über dem Buchungsformular — z. B. bei Betriebsurlaub oder geschlossenen Tagen. Optional nur in einem Zeitraum anzeigen.</p>
      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={!!s.booking_notice_enabled} onChange={e => setS({ ...s, booking_notice_enabled: e.target.checked })} data-testid="g-notice-enabled" className="w-4 h-4" />
          Info-Tafel anzeigen
        </label>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Hinweistext</label>
          <textarea
            value={s.booking_notice_text || ''}
            onChange={e => setS({ ...s, booking_notice_text: e.target.value })}
            placeholder="z. B. Betriebsurlaub vom 01.–14. August. Ab 15. August sind wir wieder für Sie da!"
            rows={3}
            data-testid="g-notice-text"
            className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Anzeigen ab (optional)</label>
            <input type="date" value={s.booking_notice_from || ''} onChange={e => setS({ ...s, booking_notice_from: e.target.value })} data-testid="g-notice-from" className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Anzeigen bis (optional)</label>
            <input type="date" value={s.booking_notice_to || ''} onChange={e => setS({ ...s, booking_notice_to: e.target.value })} data-testid="g-notice-to" className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white" />
          </div>
        </div>
        <p className="text-xs text-[#9CA3AF]">Leere Datumsfelder = dauerhaft anzeigen (solange aktiviert).</p>
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Datenschutz & Datenaufbewahrung</h2>
      <p className="text-xs text-[#5C5C59] mb-3">
        Steuerung des Datenschutz-Hinweises auf der Kundenseite und automatische Anonymisierung alter
        Reservierungen gemäß DSGVO.
        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" data-testid="settings-privacy-preview" className="ml-2 underline hover:text-[#1C1C19]">Vorschau öffnen</a>
      </p>
      <div className="grid md:grid-cols-2 gap-4 p-4 bg-[#F9F9F7] rounded">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!s.privacy_checkbox_required}
            onChange={e => setS({...s, privacy_checkbox_required: e.target.checked})}
            data-testid="g-privacy-required"
            className="mt-1 w-4 h-4 accent-[#1C1C19]"
          />
          <span className="text-sm">
            <span className="block font-medium text-[#1C1C19]">Datenschutz-Bestätigung Pflicht</span>
            <span className="text-xs text-[#5C5C59]">Gast muss vor dem Absenden die Checkbox aktivieren.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!s.auto_delete_old_reservations}
            onChange={e => setS({...s, auto_delete_old_reservations: e.target.checked})}
            data-testid="g-auto-delete"
            className="mt-1 w-4 h-4 accent-[#1C1C19]"
          />
          <span className="text-sm">
            <span className="block font-medium text-[#1C1C19]">Alte Reservierungen automatisch anonymisieren</span>
            <span className="text-xs text-[#5C5C59]">Persönliche Daten werden nach Ablauf der Frist entfernt.</span>
          </span>
        </label>
        <FieldInput
          label="Speicherdauer (Tage)"
          type="number"
          value={s.reservation_retention_days}
          onChange={v => setS({...s, reservation_retention_days: parseInt(v) || 180})}
          testId="g-retention-days"
          hint="Reservierungen älter als diese Anzahl Tage werden anonymisiert (Standard 180)."
        />
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Bestätigungsmodus</h2>
      <div>
        <select value={s.confirmation_mode} onChange={e => setS({...s, confirmation_mode: e.target.value})} data-testid="g-conf-mode" className="w-full md:w-auto px-3 py-2 border border-[#E5E5E0] rounded bg-white">
          <option value="auto">Automatisch (alle Reservierungen sofort bestätigen)</option>
          <option value="manual">Manuell (alle Reservierungen warten)</option>
          <option value="mixed">Gemischt (kleine Gruppen auto, große manuell)</option>
        </select>
        {s.confirmation_mode === 'mixed' && (
          <div className="grid md:grid-cols-2 gap-4 mt-4 p-4 bg-[#F9F9F7] rounded">
            <FieldInput label="Auto bestätigen bis ... Personen" type="number" value={s.mixed_auto_max_persons} onChange={v => setS({...s, mixed_auto_max_persons: parseInt(v) || 4})} testId="g-mix-auto" />
            <FieldInput label="Manuell ab ... Personen" type="number" value={s.mixed_manual_min_persons} onChange={v => setS({...s, mixed_manual_min_persons: parseInt(v) || 8})} testId="g-mix-manual" />
          </div>
        )}
      </div>

      <h2 className="font-display text-lg mt-6 mb-2">Bereichsumbuchung</h2>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={s.notify_guest_on_rebook !== false}
          onChange={e => setS({ ...s, notify_guest_on_rebook: e.target.checked })}
          data-testid="g-notify-rebook"
        />
        Gäste per E-Mail benachrichtigen, wenn die Reservierung wegen Bereichsschließung verschoben wird
      </label>

      <AiAssistantSettings s={s} setS={setS} />

      <button onClick={save} data-testid="g-save-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-6 py-2.5 rounded text-sm flex items-center gap-2 mt-4">
        <Save className="w-4 h-4" /> Speichern
      </button>

      <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
        <h2 className="font-display text-lg mb-2">Warteliste</h2>
        <p className="text-sm text-[#5C5C59] mb-3">Gäste können sich auf die Warteliste setzen, wenn keine Slots verfügbar sind.</p>
        <label className="flex items-center gap-2 text-sm mb-2">
          <input type="checkbox" checked={s.waitlist_enabled ?? true} onChange={e => setS({...s, waitlist_enabled: e.target.checked})} data-testid="g-waitlist-enabled" />
          Warteliste aktiv
        </label>
        <div className="mt-2">
          <FieldInput label="Antwortzeit für Wartelisten-Angebote (Min.)" type="number" value={s.waitlist_response_minutes ?? 10} onChange={v => setS({...s, waitlist_response_minutes: parseInt(v) || 10})} testId="g-waitlist-response" />
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
        <h2 className="font-display text-lg mb-2">Admin-Benachrichtigungen</h2>
        <p className="text-sm text-[#5C5C59] mb-3">
          Welche E-Mail-Adresse soll Restaurant-Ereignisse (neue Reservierung, Änderung, Stornierung, No-Show) erhalten?
          Standardmäßig wird die Restaurant-E-Mail oben verwendet.
        </p>
        <FieldInput
          label="Empfänger für Admin-E-Mails (optional)"
          value={s.admin_notification_email}
          onChange={(v) => setS({ ...s, admin_notification_email: v })}
          testId="g-admin-notif-email"
        />
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={s.notify_admin_on_new ?? true} onChange={(e) => setS({ ...s, notify_admin_on_new: e.target.checked })} data-testid="g-notif-new" />
            Neue Reservierung
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={s.notify_admin_on_change ?? true} onChange={(e) => setS({ ...s, notify_admin_on_change: e.target.checked })} data-testid="g-notif-change" />
            Änderung
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={s.notify_admin_on_cancel ?? true} onChange={(e) => setS({ ...s, notify_admin_on_cancel: e.target.checked })} data-testid="g-notif-cancel" />
            Stornierung
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={s.notify_admin_on_no_show ?? true} onChange={(e) => setS({ ...s, notify_admin_on_no_show: e.target.checked })} data-testid="g-notif-noshow" />
            No-Show
          </label>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
        <h2 className="font-display text-lg mb-2">Gäste-Selbstverwaltung</h2>
        <p className="text-sm text-[#5C5C59] mb-3">
          Gäste erhalten in der Bestätigungs-E-Mail einen Link zu ihrer Reservierung, über den sie sie selbst stornieren können.
        </p>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input
            type="checkbox"
            checked={s.customer_cancel_enabled ?? true}
            onChange={(e) => setS({ ...s, customer_cancel_enabled: e.target.checked })}
            data-testid="g-cust-cancel-enabled"
          />
          Online-Stornierung durch Gast erlauben
        </label>
        <FieldInput
          label="Stornierung nur bis ... Std. vor Reservierung"
          type="number"
          value={s.customer_cancel_hours_limit ?? 6}
          onChange={(v) => setS({ ...s, customer_cancel_hours_limit: parseInt(v) || 6 })}
          testId="g-cust-cancel-hours"
        />
        <p className="text-xs text-[#5C5C59] mt-1">
          Danach erscheint ein Dialog mit der Telefonnummer und Hinweis zum Anrufen.
        </p>
      </div>

      <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
        <h2 className="font-display text-lg mb-2">Reservierungsformular</h2>
        <p className="text-sm text-[#5C5C59] mb-3">
          Gäste wählen ihre Wünsche standardmäßig aus dem Tag-System (Geburtstag, Hund, Kinderstuhl …). Bei Bedarf können Sie zusätzlich ein freies Textfeld einblenden.
        </p>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input
            type="checkbox"
            checked={s.notes_field_enabled ?? false}
            onChange={(e) => setS({ ...s, notes_field_enabled: e.target.checked })}
            data-testid="g-notes-field-enabled"
          />
          Optionales Freitextfeld „Weitere Hinweise" anzeigen
        </label>
        <FieldInput
          label="Maximale Zeichenanzahl"
          type="number"
          value={s.notes_field_max_chars ?? 200}
          onChange={(v) => setS({ ...s, notes_field_max_chars: Math.max(20, parseInt(v) || 200) })}
          testId="g-notes-field-max"
        />
        <p className="text-xs text-[#5C5C59] mt-1">
          Tags pflegen Sie unter „Tags" im Menü.
        </p>
      </div>


      <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
        <h2 className="font-display text-lg mb-2">E-Mail-Benachrichtigungen</h2>
        <p className="text-sm text-[#5C5C59] mb-3">
          Reservierungen werden automatisch per E-Mail bestätigt (sofern Gast eine E-Mail angibt). Neue Anfragen gehen an die Restaurant-E-Mail.
        </p>
        <div className="bg-[#F9F9F7] border border-[#E5E5E0] rounded p-3 mb-3 text-xs space-y-1.5">
          <div className="font-medium text-[#1C1C19]">In <span className="font-mono">backend/.env</span> setzen (eine Option wählen):</div>
          <div><span className="font-mono text-[#5C5C59]">Gmail SMTP:</span> <span className="font-mono">SMTP_HOST=smtp.gmail.com</span>, <span className="font-mono">SMTP_PORT=587</span>, <span className="font-mono">SMTP_USER=ihre@gmail.com</span>, <span className="font-mono">SMTP_PASSWORD=app_passwort</span> (Gmail-App-Passwort)</div>
          <div><span className="font-mono text-[#5C5C59]">Resend:</span> <span className="font-mono">RESEND_API_KEY=re_...</span></div>
          <div className="text-[#5C5C59]">SMTP hat Vorrang, wenn beides konfiguriert ist. Backend nach Änderung neu starten.</div>
        </div>
        <button onClick={testEmail} disabled={testingEmail} data-testid="test-email-btn" className="px-4 py-2 border border-[#1C1C19] rounded text-sm hover:bg-[#1C1C19] hover:text-white transition disabled:opacity-50">
          {testingEmail ? 'Sende...' : 'Test-E-Mail senden'}
        </button>
        {emailResult && (
          <p className={`mt-3 text-sm ${emailResult.ok ? 'text-[#22C55E]' : 'text-[#EAB308]'}`} data-testid="test-email-result">
            {emailResult.ok ? `✓ Test-E-Mail an ${emailResult.recipient} gesendet (${emailResult.provider}).` : `⚠ ${emailResult.reason || 'Versand fehlgeschlagen.'}`}
          </p>
        )}
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = 'text', testId, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]" />
      {hint && <p className="text-xs text-[#5C5C59] mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

function OpeningHoursTab() {
  const [hours, setHours] = useState([]);
  const [cfg, setCfg] = useState(null);          // seasonal settings
  const [season, setSeason] = useState('summer'); // active sub-tab when seasonal on
  const [savingCfg, setSavingCfg] = useState(false);

  const loadHours = async (seasonParam) => {
    const url = seasonParam ? `/opening-hours?season=${seasonParam}` : '/opening-hours';
    const r = await api.get(url);
    setHours(r.data);
  };
  const loadCfg = async () => {
    const r = await api.get('/settings');
    setCfg({
      seasonal_hours_enabled: !!r.data.seasonal_hours_enabled,
      season_override: r.data.season_override || 'auto',
      summer_start: r.data.summer_start || '04-01',
      summer_end: r.data.summer_end || '10-31',
    });
    return r.data;
  };

  useEffect(() => {
    (async () => {
      const s = await loadCfg();
      await loadHours(s.seasonal_hours_enabled ? 'summer' : null);
    })();
  }, []);

  // Reload the weekly grid whenever the active season (or seasonal switch) changes.
  useEffect(() => {
    if (!cfg) return;
    loadHours(cfg.seasonal_hours_enabled ? season : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season, cfg?.seasonal_hours_enabled]);

  const saveDay = async (wd, data) => {
    try {
      const q = cfg?.seasonal_hours_enabled ? `?season=${season}` : '';
      await api.put(`/opening-hours/${wd}${q}`, data);
      toast.success('Gespeichert');
      loadHours(cfg?.seasonal_hours_enabled ? season : null);
    } catch { toast.error('Fehler'); }
  };

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSavingCfg(true);
    try {
      await api.put('/settings', patch);
      toast.success('Saison-Einstellungen gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    setSavingCfg(false);
  };

  return (
    <div className="space-y-4" data-testid="settings-hours">
      {/* Seasonal configuration */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-4 space-y-3" data-testid="seasonal-config">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={!!cfg?.seasonal_hours_enabled}
            onChange={e => saveCfg({ seasonal_hours_enabled: e.target.checked })}
            data-testid="seasonal-enabled"
          />
          Getrennte Sommer-/Winter-Öffnungszeiten verwenden
        </label>
        <p className="text-[12px] text-[#5C5C59]">Wenn aktiv, pflegst du zwei komplette Wochenpläne. Sondertage und Service-Wellen haben weiterhin Vorrang.</p>

        {cfg?.seasonal_hours_enabled && (
          <div className="space-y-3 pt-1">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Aktive Saison</label>
              <select
                value={cfg.season_override}
                onChange={e => saveCfg({ season_override: e.target.value })}
                data-testid="season-override"
                className="px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white w-full sm:w-72"
              >
                <option value="auto">Automatisch nach Datum</option>
                <option value="summer">Immer Sommerzeit</option>
                <option value="winter">Immer Winterzeit</option>
              </select>
            </div>
            {cfg.season_override === 'auto' && (
              <div className="flex flex-wrap items-end gap-3">
                <MonthDayPicker label="Sommer von" value={cfg.summer_start} onChange={v => saveCfg({ summer_start: v })} testId="summer-start" />
                <MonthDayPicker label="Sommer bis" value={cfg.summer_end} onChange={v => saveCfg({ summer_end: v })} testId="summer-end" />
                <span className="text-[12px] text-[#5C5C59] pb-2">Außerhalb dieses Zeitraums gilt der Winter-Plan.</span>
              </div>
            )}
            {/* Season sub-tabs */}
            <div className="flex gap-2 pt-1" data-testid="season-tabs">
              {[['summer', 'Sommer'], ['winter', 'Winter']].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setSeason(k)}
                  data-testid={`season-tab-${k}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${season === k ? 'bg-[#1C1C19] text-white' : 'bg-[#F1F1ED] text-[#5C5C59] hover:bg-[#E5E5E0]'}`}
                >
                  {l}
                </button>
              ))}
              {savingCfg && <span className="text-xs text-[#9CA3AF] self-center">speichern…</span>}
            </div>
          </div>
        )}
      </div>

      {/* Weekly schedule (default or per-season) */}
      <div className="space-y-3">
        {cfg?.seasonal_hours_enabled && (
          <h2 className="font-display text-xl">{season === 'summer' ? 'Sommer-Öffnungszeiten' : 'Winter-Öffnungszeiten'}</h2>
        )}
        {hours.map(h => <DayHourRow key={`${season}-${h.weekday}`} day={h} onSave={d => saveDay(h.weekday, d)} />)}
      </div>
    </div>
  );
}

function MonthDayPicker({ label, value, onChange, testId }) {
  const [mm, dd] = (value || '04-01').split('-');
  const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const set = (m, d) => onChange(`${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">{label}</label>
      <div className="flex gap-1.5">
        <select value={parseInt(mm)} onChange={e => set(parseInt(e.target.value), parseInt(dd))} data-testid={`${testId}-month`} className="px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={parseInt(dd)} onChange={e => set(parseInt(mm), parseInt(e.target.value))} data-testid={`${testId}-day`} className="px-2 py-2 border border-[#E5E5E0] rounded text-sm bg-white">
          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
    </div>
  );
}

function DayHourRow({ day, onSave }) {
  const [closed, setClosed] = useState(day.closed);
  const [blocks, setBlocks] = useState(day.blocks || []);

  useEffect(() => {
    setClosed(day.closed);
    setBlocks(day.blocks || []);
  }, [day]);

  const addBlock = () => setBlocks([...blocks, { start_time: '17:30', end_time: '22:00' }]);
  const removeBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const updateBlock = (i, field, value) => setBlocks(blocks.map((b, idx) => idx === i ? { ...b, [field]: value } : b));
  const save = () => onSave({ weekday: day.weekday, closed, blocks });

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid={`hours-day-${day.weekday}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg">{WEEKDAY_LABELS[day.weekday]}</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={closed} onChange={e => setClosed(e.target.checked)} data-testid={`hours-closed-${day.weekday}`} />
          Ruhetag
        </label>
      </div>
      {!closed && (
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`hours-block-${day.weekday}-${i}`}>
              <input type="time" value={b.start_time} onChange={e => updateBlock(i, 'start_time', e.target.value)} data-testid={`hours-start-${day.weekday}-${i}`} className="px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-[#5C5C59]">–</span>
              <input type="time" value={b.end_time} onChange={e => updateBlock(i, 'end_time', e.target.value)} data-testid={`hours-end-${day.weekday}-${i}`} className="px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
              <button onClick={() => removeBlock(i)} data-testid={`hours-remove-${day.weekday}-${i}`} className="p-2 hover:bg-[#FEE2E2] rounded text-[#EF4444]">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button onClick={addBlock} data-testid={`hours-add-${day.weekday}`} className="text-sm text-[#5C5C59] hover:text-[#1C1C19] flex items-center gap-1">
            <Plus className="w-3 h-3" /> Zeitblock hinzufügen
          </button>
        </div>
      )}
      <button onClick={save} data-testid={`hours-save-${day.weekday}`} className="mt-3 bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-1.5 rounded text-sm">
        Speichern
      </button>
    </div>
  );
}

function SpecialDaysTab() {
  const [days, setDays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: '', type: 'closed', blocks: [], waves: [], note: '', guest_notice_enabled: false, guest_notice_text: '' });

  const load = async () => { const r = await api.get('/special-days'); setDays(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.date) { toast.error('Datum erforderlich'); return; }
    try {
      await api.post('/special-days', form);
      toast.success('Hinzugefügt');
      setShowForm(false);
      setForm({ date: '', type: 'closed', blocks: [], waves: [], note: '', guest_notice_enabled: false, guest_notice_text: '' });
      load();
    } catch { toast.error('Fehler'); }
  };
  const del = async (id) => {
    if (!window.confirm('Löschen?')) return;
    await api.delete(`/special-days/${id}`);
    toast.success('Gelöscht');
    load();
  };

  return (
    <div data-testid="settings-special">
      <button onClick={() => setShowForm(true)} data-testid="sd-add-btn" className="mb-4 bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm flex items-center gap-2">
        <Plus className="w-4 h-4" /> Sondertag hinzufügen
      </button>

      <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
        {days.length === 0 && <div className="p-8 text-center text-[#5C5C59]" data-testid="sd-empty">Keine Sondertage definiert.</div>}
        {days.map(d => (
          <div key={d.id} className="p-4 flex items-center gap-4" data-testid={`sd-row-${d.id}`}>
            <div className="font-mono text-sm">{formatDateShort(d.date)}</div>
            <div className="flex-1">
              <div className="text-sm font-medium">
                {d.type === 'closed' ? 'Geschlossen' : 'Sonderöffnung'}
                {d.type === 'special_open' && d.waves?.length > 0 && (
                  <span className="text-xs text-[#5C5C59] font-normal ml-2">
                    {d.waves.map(w => `${w.name || 'Welle'}: ${w.first_arrival}–${w.last_arrival} → Ende ${w.end_time}`).join(' · ')}
                  </span>
                )}
                {d.type === 'special_open' && (!d.waves || d.waves.length === 0) && d.blocks?.length > 0 && (
                  <span className="text-xs text-[#5C5C59] font-normal ml-2">
                    {d.blocks.map(b => `${b.start_time}-${b.end_time}`).join(', ')}
                  </span>
                )}
              </div>
              {d.note && <div className="text-xs text-[#5C5C59]">{d.note}</div>}
              {d.type === 'closed' && d.guest_notice_enabled && d.guest_notice_text && (
                <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#B45309] bg-[#B45309]/10 px-2 py-0.5 rounded" data-testid={`sd-notice-badge-${d.id}`} title={d.guest_notice_text}>
                  <Info className="w-3 h-3" /> Info im Formular
                </div>
              )}
            </div>
            <button onClick={() => del(d.id)} data-testid={`sd-delete-${d.id}`} className="p-2 hover:bg-[#FEE2E2] rounded">
              <Trash2 className="w-4 h-4 text-[#EF4444]" />
            </button>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="sd-form">
            <h2 className="font-display text-xl mb-4">Sondertag</h2>
            <div className="space-y-3">
              <FieldInput label="Datum" type="date" value={form.date} onChange={v => setForm({...form, date: v})} testId="sd-form-date" />
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Typ</label>
                <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} data-testid="sd-form-type" className="w-full px-3 py-2 border border-[#E5E5E0] rounded bg-white">
                  <option value="closed">Geschlossen</option>
                  <option value="special_open">Sonderöffnung</option>
                </select>
              </div>
              {form.type === 'special_open' && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Öffnungszeiten</label>
                  {form.blocks.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <input type="time" value={b.start_time} onChange={e => setForm({...form, blocks: form.blocks.map((x, idx) => idx === i ? {...x, start_time: e.target.value} : x)})} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
                      <span>–</span>
                      <input type="time" value={b.end_time} onChange={e => setForm({...form, blocks: form.blocks.map((x, idx) => idx === i ? {...x, end_time: e.target.value} : x)})} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
                      <button onClick={() => setForm({...form, blocks: form.blocks.filter((_, idx) => idx !== i)})} className="p-1 text-[#EF4444]"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <button onClick={() => setForm({...form, blocks: [...form.blocks, { start_time: '17:30', end_time: '22:00' }]})} data-testid="sd-form-add-block" className="text-sm text-[#5C5C59] hover:text-[#1C1C19] flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Block
                  </button>
                </div>
              )}
              {form.type === 'special_open' && (
                <div className="border-t border-[#E5E5E0] pt-3">
                  <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Service-Wellen (optional)</label>
                  <p className="text-[11px] text-[#9CA3AF] mb-2">Gäste wählen eine Ankunftszeit im Fenster; alle Buchungen einer Welle enden zur festen Endzeit. Wellen ersetzen nur die Öffnungszeiten, die sich mit dem Wellen-Fenster überschneiden – Öffnungszeiten außerhalb der Wellen (z. B. die Mittagszeit) bleiben als normale Buchungszeiten erhalten.</p>
                  {(form.waves || []).map((w, i) => {
                    const upd = (field, value) => setForm({...form, waves: form.waves.map((x, idx) => idx === i ? {...x, [field]: value} : x)});
                    return (
                      <div key={i} className="border border-[#E5E5E0] rounded p-2 mb-2 space-y-2" data-testid={`sd-form-wave-${i}`}>
                        <div className="flex items-center gap-2">
                          <input value={w.name || ''} onChange={e => upd('name', e.target.value)} placeholder={`Name (z. B. ${i + 1}. Welle)`} data-testid={`sd-form-wave-name-${i}`} className="flex-1 px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
                          <button onClick={() => setForm({...form, waves: form.waves.filter((_, idx) => idx !== i)})} data-testid={`sd-form-wave-remove-${i}`} className="p-1 text-[#EF4444]"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap text-xs text-[#5C5C59]">
                          <span>Ankunft</span>
                          <input type="time" value={w.first_arrival || ''} onChange={e => upd('first_arrival', e.target.value)} data-testid={`sd-form-wave-first-${i}`} className="px-2 py-1 border border-[#E5E5E0] rounded" />
                          <span>bis</span>
                          <input type="time" value={w.last_arrival || ''} onChange={e => upd('last_arrival', e.target.value)} data-testid={`sd-form-wave-last-${i}`} className="px-2 py-1 border border-[#E5E5E0] rounded" />
                          <span>alle</span>
                          <input type="number" min="5" step="5" value={w.interval_minutes || 15} onChange={e => upd('interval_minutes', parseInt(e.target.value) || 15)} data-testid={`sd-form-wave-interval-${i}`} className="w-16 px-2 py-1 border border-[#E5E5E0] rounded" />
                          <span>Min · Ende</span>
                          <input type="time" value={w.end_time || ''} onChange={e => upd('end_time', e.target.value)} data-testid={`sd-form-wave-end-${i}`} className="px-2 py-1 border border-[#E5E5E0] rounded" />
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={() => setForm({...form, waves: [...(form.waves || []), { name: `${(form.waves || []).length + 1}. Welle`, first_arrival: '17:30', last_arrival: '18:30', interval_minutes: 15, end_time: '20:00' }]})} data-testid="sd-form-add-wave" className="text-sm text-[#5C5C59] hover:text-[#1C1C19] flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Welle hinzufügen
                  </button>
                </div>
              )}
              <FieldInput label="Notiz (optional)" value={form.note} onChange={v => setForm({...form, note: v})} testId="sd-form-note" />
              {form.type === 'closed' && (
                <div className="border-t border-[#E5E5E0] pt-3">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="sd-form-notice-toggle-label">
                    <input
                      type="checkbox"
                      checked={!!form.guest_notice_enabled}
                      onChange={e => setForm({...form, guest_notice_enabled: e.target.checked})}
                      data-testid="sd-form-notice-enabled"
                      className="w-4 h-4 accent-[#1C1C19]"
                    />
                    <span className="text-sm font-medium text-[#1C1C19]">Info im Online-Formular anzeigen</span>
                  </label>
                  <p className="text-[11px] text-[#9CA3AF] mt-1">Wird Gästen angezeigt, wenn sie dieses (geschlossene) Datum im Buchungsformular auswählen.</p>
                  {form.guest_notice_enabled && (
                    <textarea
                      value={form.guest_notice_text}
                      onChange={e => setForm({...form, guest_notice_text: e.target.value})}
                      data-testid="sd-form-notice-text"
                      rows={3}
                      maxLength={500}
                      placeholder="z. B. Am 24.12. bleibt unser Restaurant geschlossen. Wir wünschen frohe Weihnachten!"
                      className="mt-2 w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm resize-y"
                    />
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowForm(false)} data-testid="sd-form-cancel" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm">Abbrechen</button>
              <button onClick={save} data-testid="sd-form-save" className="bg-[#1C1C19] text-white px-4 py-2 rounded text-sm">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DurationTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/settings').then(r => setS(r.data)); }, []);
  if (!s) return null;

  const addRule = () => setS({...s, duration_rules: [...(s.duration_rules || []), { min_persons: 1, max_persons: 2, duration_minutes: 90 }]});
  const updateRule = (i, field, v) => setS({...s, duration_rules: s.duration_rules.map((r, idx) => idx === i ? {...r, [field]: parseInt(v) || 0} : r)});
  const removeRule = (i) => setS({...s, duration_rules: s.duration_rules.filter((_, idx) => idx !== i)});

  // Guest-selectable duration (per weekday) config
  const WD = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  const COMMON = [60, 90, 120, 150, 180, 210, 240];
  const fmtDur = (m) => m % 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')} Std` : `${m / 60} Std`;
  const getDurs = (wd) => (s.guest_duration_options || []).find(r => r.weekday === wd)?.durations || [];
  const setDurs = (wd, durs) => {
    const others = (s.guest_duration_options || []).filter(r => r.weekday !== wd);
    const cleaned = [...new Set(durs.filter(d => d > 0))].sort((a, b) => a - b);
    setS({ ...s, guest_duration_options: cleaned.length ? [...others, { weekday: wd, durations: cleaned }] : others });
  };
  const toggleDur = (wd, d) => {
    const cur = getDurs(wd);
    setDurs(wd, cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d]);
  };

  const save = async () => {
    try {
      await api.put('/settings', {
        duration_rules: s.duration_rules,
        guest_duration_enabled: !!s.guest_duration_enabled,
        guest_duration_options: s.guest_duration_options || [],
      });
      toast.success('Gespeichert');
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6" data-testid="settings-duration">
      <p className="text-sm text-[#5C5C59] mb-4">
        Definieren Sie unterschiedliche Aufenthaltsdauern nach Personenzahl. Wenn keine Regel passt, gilt die Standard-Aufenthaltsdauer.
      </p>
      <div className="space-y-2 mb-4">
        {(s.duration_rules || []).map((r, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`dur-rule-${i}`}>
            <input type="number" min="1" value={r.min_persons} onChange={e => updateRule(i, 'min_persons', e.target.value)} className="w-20 px-2 py-1.5 border border-[#E5E5E0] rounded text-sm" placeholder="Min" />
            <span className="text-sm text-[#5C5C59]">–</span>
            <input type="number" min="1" value={r.max_persons} onChange={e => updateRule(i, 'max_persons', e.target.value)} className="w-20 px-2 py-1.5 border border-[#E5E5E0] rounded text-sm" placeholder="Max" />
            <span className="text-sm text-[#5C5C59]">Personen:</span>
            <input type="number" min="30" step="15" value={r.duration_minutes} onChange={e => updateRule(i, 'duration_minutes', e.target.value)} className="w-24 px-2 py-1.5 border border-[#E5E5E0] rounded text-sm" />
            <span className="text-sm text-[#5C5C59]">Min.</span>
            <button onClick={() => removeRule(i)} className="p-1.5 text-[#EF4444] hover:bg-[#FEE2E2] rounded"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={addRule} data-testid="dur-add-rule" className="text-sm text-[#5C5C59] hover:text-[#1C1C19] flex items-center gap-1 mb-4">
        <Plus className="w-3 h-3" /> Regel hinzufügen
      </button>

      <div className="border-t border-[#E5E5E0] pt-5 mt-1 mb-4">
        <label className="flex items-center gap-2 mb-1 cursor-pointer">
          <input type="checkbox" checked={!!s.guest_duration_enabled} onChange={e => setS({...s, guest_duration_enabled: e.target.checked})} data-testid="guest-dur-enabled" className="w-4 h-4 accent-[#1C1C19]" />
          <span className="text-sm font-medium text-[#1C1C19]">Gäste dürfen die Aufenthaltsdauer online selbst wählen</span>
        </label>
        <p className="text-xs text-[#5C5C59] mb-3 ml-6">Pro Wochentag wählbare Dauern festlegen (nur Online-Buchung). Es wird nie eine längere Dauer angeboten, als der Tisch tatsächlich frei ist.</p>
        {s.guest_duration_enabled && (
          <div className="space-y-2">
            {WD.map((label, wd) => (
              <div key={wd} className="flex flex-wrap items-center gap-2" data-testid={`guest-dur-day-${wd}`}>
                <span className="text-sm text-[#5C5C59] w-24 shrink-0">{label}</span>
                {COMMON.map(d => {
                  const on = getDurs(wd).includes(d);
                  return (
                    <button key={d} type="button" onClick={() => toggleDur(wd, d)} data-testid={`guest-dur-${wd}-${d}`}
                      className={`px-3 py-1 rounded-full border text-xs transition-colors ${on ? 'bg-[#1C1C19] border-[#1C1C19] text-white' : 'bg-white border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}>
                      {fmtDur(d)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} data-testid="dur-save-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-6 py-2 rounded text-sm flex items-center gap-2">
        <Save className="w-4 h-4" /> Speichern
      </button>
    </div>
  );
}

function EmailTemplateTab() {
  const [s, setS] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => { api.get('/settings').then(r => setS(r.data)); }, []);

  // Live preview (debounced)
  useEffect(() => {
    if (!s) return;
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await api.post('/settings/email-preview-draft', {
          email_header_image_url: s.email_header_image_url || '',
          email_footer_text: s.email_footer_text || '',
          email_accent_color: s.email_accent_color || '#EAB308',
        });
        setPreviewHtml(r.data.html);
      } catch {} finally { setPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [s?.email_header_image_url, s?.email_footer_text, s?.email_accent_color, s]);

  if (!s) return <div>Lädt...</div>;

  const save = async () => {
    try {
      await api.put('/settings', {
        email_header_image_url: s.email_header_image_url || '',
        email_footer_text: s.email_footer_text || '',
        email_accent_color: s.email_accent_color || '#EAB308',
        reminder_enabled: s.reminder_enabled,
        reminder_hours_before: parseInt(s.reminder_hours_before) || 24,
        reminder_send_hour: parseInt(s.reminder_send_hour ?? 9),
      });
      toast.success('Gespeichert');
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="settings-email-template">
      {/* Editor */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4">
        <h2 className="font-display text-lg mb-2">Vorlage anpassen</h2>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Header-Bild</label>
          {s.email_header_image_url && (
            <img src={s.email_header_image_url} alt="" className="w-full max-h-32 object-cover rounded border border-[#E5E5E0] mb-2" data-testid="email-header-preview" />
          )}
          <input type="text" value={s.email_header_image_url || ''} onChange={e => setS({...s, email_header_image_url: e.target.value})} data-testid="email-header-url" placeholder="https://... oder data:image/..." className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
          <div className="flex gap-2 mt-2">
            <label className="px-3 py-1.5 border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white rounded text-xs cursor-pointer transition" data-testid="email-header-upload">
              Bild hochladen
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 1_500_000) { toast.error('Maximal 1.5 MB'); return; }
                const reader = new FileReader();
                reader.onload = (ev) => setS({...s, email_header_image_url: ev.target.result});
                reader.readAsDataURL(file);
              }} />
            </label>
            {s.email_header_image_url && (
              <button onClick={() => setS({...s, email_header_image_url: ''})} data-testid="email-header-clear" className="px-3 py-1.5 border border-[#E5E5E0] hover:border-[#EF4444] text-[#5C5C59] hover:text-[#EF4444] rounded text-xs">Entfernen</button>
            )}
          </div>
          <p className="text-[10px] text-[#5C5C59] mt-1">Max. 1.5 MB. Erscheint ganz oben in jeder E-Mail.</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Footer-Text</label>
          <textarea
            value={s.email_footer_text || ''}
            onChange={e => setS({...s, email_footer_text: e.target.value})}
            data-testid="email-footer-text"
            rows={4}
            className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono resize-none"
            placeholder="La Contadina · Dresden · Tel: ...&#10;www.lacontadina-dresden.de"
          />
          <p className="text-[10px] text-[#5C5C59] mt-1">Zeilenumbrüche werden übernommen.</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Akzentfarbe</label>
          <div className="flex gap-2 items-center">
            <input type="color" value={s.email_accent_color || '#EAB308'} onChange={e => setS({...s, email_accent_color: e.target.value})} data-testid="email-accent-color" className="w-12 h-9 border border-[#E5E5E0] rounded cursor-pointer" />
            <input type="text" value={s.email_accent_color || '#EAB308'} onChange={e => setS({...s, email_accent_color: e.target.value})} className="px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono w-32" />
          </div>
        </div>

        <div className="pt-4 border-t border-[#E5E5E0]">
          <h3 className="font-display text-base mb-2">Reservierungs-Erinnerung</h3>
          <p className="text-xs text-[#5C5C59] mb-2">Automatische E-Mail-Erinnerung an den Gast einen Tag vor der Reservierung. Wird einmal täglich zur konfigurierten Uhrzeit versendet.</p>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={s.reminder_enabled ?? true} onChange={e => setS({...s, reminder_enabled: e.target.checked})} data-testid="reminder-enabled" />
            Erinnerungen aktiv
          </label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Stunden vor Reservierung</label>
              <input type="number" min="1" max="168" value={s.reminder_hours_before ?? 24} onChange={e => setS({...s, reminder_hours_before: parseInt(e.target.value) || 24})} data-testid="reminder-hours" className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Versand-Uhrzeit (Stunde)</label>
              <input type="number" min="0" max="23" value={s.reminder_send_hour ?? 9} onChange={e => setS({...s, reminder_send_hour: Math.max(0, Math.min(23, parseInt(e.target.value) || 9))})} data-testid="reminder-send-hour" className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
            </div>
          </div>
        </div>

        <button onClick={save} data-testid="email-template-save" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-6 py-2.5 rounded text-sm flex items-center gap-2">
          <Save className="w-4 h-4" /> Speichern
        </button>
      </div>

      {/* Live preview */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden flex flex-col" data-testid="email-preview-pane">
        <div className="px-4 py-2 border-b border-[#E5E5E0] flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-[#5C5C59]">Live-Vorschau (Bestätigungs-E-Mail)</div>
          {previewLoading && <div className="text-xs text-[#5C5C59]">Aktualisiere...</div>}
        </div>
        <iframe
          title="email-preview"
          srcDoc={previewHtml}
          data-testid="email-preview-iframe"
          className="flex-1 w-full min-h-[600px] border-0 bg-[#F9F9F7]"
        />
      </div>
    </div>
  );
}


function FeedbackTab() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);

  useEffect(() => {
    api.get('/settings').then(r => setS(r.data)).catch(() => toast.error('Fehler beim Laden'));
  }, []);

  if (!s) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings', {
        feedback_enabled: !!s.feedback_enabled,
        feedback_send_delay_type: s.feedback_send_delay_type || '1d',
        feedback_send_delay_hours: parseInt(s.feedback_send_delay_hours ?? 1),
        feedback_send_next_day_time: s.feedback_send_next_day_time || '10:00',
        feedback_link_valid_days: parseInt(s.feedback_link_valid_days ?? 14),
        feedback_reminder_active: !!s.feedback_reminder_active,
        feedback_reminder_days: parseInt(s.feedback_reminder_days ?? 3),
        feedback_email_subject: s.feedback_email_subject || '',
        feedback_email_body: s.feedback_email_body || '',
        feedback_notify_admin_on_new: !!s.feedback_notify_admin_on_new,
        feedback_notify_admin_on_low_rating: !!s.feedback_notify_admin_on_low_rating,
        feedback_low_rating_threshold: parseInt(s.feedback_low_rating_threshold ?? 2),
        google_review_enabled: !!s.google_review_enabled,
        google_review_url: s.google_review_url || '',
        google_review_min_rating: parseInt(s.google_review_min_rating ?? 4),
        briefing_enabled: !!s.briefing_enabled,
        briefing_send_hour: parseInt(s.briefing_send_hour ?? 20),
        briefing_email: s.briefing_email || '',
      });
      toast.success('Gespeichert');
    } catch { toast.error('Fehler'); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunBusy(true);
    try {
      await api.post('/admin/run-feedback-dispatcher');
      toast.success('Feedback-Dispatcher ausgeführt');
    } catch { toast.error('Fehler'); }
    finally { setRunBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-6 max-w-3xl" data-testid="feedback-tab">
      <div>
        <h2 className="font-display text-xl mb-1">Feedback nach dem Besuch</h2>
        <p className="text-sm text-[#5C5C59]">
          Automatische E-Mail an Gäste nach abgeschlossener Reservierung. Bewertungen erscheinen unter „Bewertungen".
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm font-medium" data-testid="fb-settings-enabled-label">
        <input
          type="checkbox"
          checked={s.feedback_enabled ?? true}
          onChange={e => setS({ ...s, feedback_enabled: e.target.checked })}
          data-testid="fb-settings-enabled"
        />
        Feedback-System aktiv
      </label>

      <div className="pt-4 border-t border-[#E5E5E0]">
        <h3 className="font-display text-base mb-3">Versandzeitpunkt</h3>
        <p className="text-xs text-[#5C5C59] mb-3">Wann nach Abschluss der Reservierung soll die E-Mail verschickt werden?</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { v: 'immediate', l: 'Sofort' },
            { v: '1h', l: '1 Std danach' },
            { v: '3h', l: '3 Std danach' },
            { v: '1d', l: 'Am nächsten Tag' },
            { v: 'custom', l: 'Benutzerdefiniert' },
          ].map(opt => (
            <button
              key={opt.v}
              onClick={() => setS({ ...s, feedback_send_delay_type: opt.v })}
              data-testid={`fb-delay-${opt.v}`}
              className={`px-3 py-2 rounded text-sm border ${
                s.feedback_send_delay_type === opt.v
                  ? 'bg-[#1C1C19] text-white border-[#1C1C19]'
                  : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'
              }`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        {s.feedback_send_delay_type === '1d' && (
          <div className="mt-3">
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Uhrzeit am Folgetag</label>
            <input
              type="time"
              value={s.feedback_send_next_day_time || '10:00'}
              onChange={e => setS({ ...s, feedback_send_next_day_time: e.target.value })}
              data-testid="fb-next-day-time"
              className="px-3 py-2 border border-[#E5E5E0] rounded text-sm"
            />
          </div>
        )}
        {s.feedback_send_delay_type === 'custom' && (
          <div className="mt-3">
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Stunden nach Reservierungsende</label>
            <input
              type="number" min="1" max="240"
              value={s.feedback_send_delay_hours ?? 1}
              onChange={e => setS({ ...s, feedback_send_delay_hours: parseInt(e.target.value) || 1 })}
              data-testid="fb-custom-hours"
              className="w-32 px-3 py-2 border border-[#E5E5E0] rounded text-sm"
            />
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-[#E5E5E0]">
        <h3 className="font-display text-base mb-3">Link & Erinnerung</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Link gültig (Tage)</label>
            <input
              type="number" min="1" max="365"
              value={s.feedback_link_valid_days ?? 14}
              onChange={e => setS({ ...s, feedback_link_valid_days: parseInt(e.target.value) || 14 })}
              data-testid="fb-link-days"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Erinnerung nach (Tage)</label>
            <input
              type="number" min="1" max="30"
              value={s.feedback_reminder_days ?? 3}
              onChange={e => setS({ ...s, feedback_reminder_days: parseInt(e.target.value) || 3 })}
              data-testid="fb-reminder-days"
              disabled={!s.feedback_reminder_active}
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm disabled:opacity-50"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm mt-3">
          <input
            type="checkbox"
            checked={s.feedback_reminder_active ?? false}
            onChange={e => setS({ ...s, feedback_reminder_active: e.target.checked })}
            data-testid="fb-reminder-active"
          />
          Erinnerung senden, wenn nach X Tagen kein Feedback abgegeben wurde
        </label>
      </div>

      <div className="pt-4 border-t border-[#E5E5E0]">
        <h3 className="font-display text-base mb-3">E-Mail-Inhalt</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Betreff</label>
            <input
              value={s.feedback_email_subject ?? ''}
              onChange={e => setS({ ...s, feedback_email_subject: e.target.value })}
              data-testid="fb-subject-input"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
              placeholder="Wie war Ihr Besuch bei uns?"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Nachricht</label>
            <textarea
              rows={4}
              value={s.feedback_email_body ?? ''}
              onChange={e => setS({ ...s, feedback_email_body: e.target.value })}
              data-testid="fb-body-input"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm resize-none"
              placeholder="Vielen Dank für Ihren Besuch..."
            />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-[#E5E5E0]">
        <h3 className="font-display text-base mb-3">Admin-Benachrichtigung</h3>
        <label className="flex items-center gap-2 text-sm mb-2">
          <input
            type="checkbox"
            checked={s.feedback_notify_admin_on_new ?? true}
            onChange={e => setS({ ...s, feedback_notify_admin_on_new: e.target.checked })}
            data-testid="fb-notify-new"
          />
          Bei jeder neuen Bewertung
        </label>
        <label className="flex items-center gap-2 text-sm mb-2">
          <input
            type="checkbox"
            checked={s.feedback_notify_admin_on_low_rating ?? true}
            onChange={e => setS({ ...s, feedback_notify_admin_on_low_rating: e.target.checked })}
            data-testid="fb-notify-low"
          />
          Sofort bei niedriger Bewertung (E-Mail)
        </label>
        <div className="flex items-center gap-2 mt-2">
          <label className="text-xs text-[#5C5C59]">Schwellwert für „niedrig":</label>
          <select
            value={s.feedback_low_rating_threshold ?? 2}
            onChange={e => setS({ ...s, feedback_low_rating_threshold: parseInt(e.target.value) })}
            data-testid="fb-threshold"
            className="px-2 py-1 border border-[#E5E5E0] rounded text-sm bg-white"
          >
            {[1, 2, 3].map(n => <option key={n} value={n}>≤ {n} Sterne</option>)}
          </select>
        </div>
      </div>

      {/* Google-Bewertungs-Funnel */}
      <div className="pt-4 border-t border-[#E5E5E0]" data-testid="fb-google-section">
        <h3 className="font-display text-base mb-1">Google-Bewertungs-Funnel</h3>
        <p className="text-xs text-[#5C5C59] mb-3">
          Bei einer guten internen Bewertung wird der Gast auf der Danke-Seite gebeten, eine Google-Bewertung zu hinterlassen. Bei niedrigen Bewertungen bleibt das Feedback intern.
        </p>
        <label className="flex items-center gap-2 text-sm font-medium mb-3">
          <input
            type="checkbox"
            checked={!!s.google_review_enabled}
            onChange={e => setS({ ...s, google_review_enabled: e.target.checked })}
            data-testid="fb-google-enabled"
          />
          Google-Bewertungs-Funnel aktiv
        </label>
        {s.google_review_enabled && (
          <div className="space-y-3 ml-1">
            <div>
              <label className="text-xs text-[#5C5C59] block mb-1">Google-Bewertungs-Link</label>
              <input
                type="url"
                value={s.google_review_url || ''}
                onChange={e => setS({ ...s, google_review_url: e.target.value })}
                placeholder="https://g.page/r/…/review"
                data-testid="fb-google-url"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
              />
              <p className="text-[11px] text-[#9CA3AF] mt-1">In Google Unternehmensprofil → „Mehr Rezensionen erhalten" → Link kopieren.</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#5C5C59]">Ab Bewertung:</label>
              <select
                value={s.google_review_min_rating ?? 4}
                onChange={e => setS({ ...s, google_review_min_rating: parseInt(e.target.value) })}
                data-testid="fb-google-min-rating"
                className="px-2 py-1 border border-[#E5E5E0] rounded text-sm bg-white"
              >
                {[3, 4, 5].map(n => <option key={n} value={n}>≥ {n} Sterne</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Vortags-Briefing */}
      <div className="pt-4 border-t border-[#E5E5E0]" data-testid="fb-briefing-section">
        <h3 className="font-display text-base mb-1">Vortags-Briefing (Team-E-Mail)</h3>
        <p className="text-xs text-[#5C5C59] mb-3">
          Automatische E-Mail am Abend vorher mit allen Reservierungen des nächsten Tages (Zeiten, Gäste, große Gruppen, Allergien/Anlässe, Stammgäste).
        </p>
        <label className="flex items-center gap-2 text-sm font-medium mb-3">
          <input
            type="checkbox"
            checked={!!s.briefing_enabled}
            onChange={e => setS({ ...s, briefing_enabled: e.target.checked })}
            data-testid="fb-briefing-enabled"
          />
          Vortags-Briefing aktiv
        </label>
        {s.briefing_enabled && (
          <div className="space-y-3 ml-1">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#5C5C59]">Versand ab Uhrzeit:</label>
              <select
                value={s.briefing_send_hour ?? 20}
                onChange={e => setS({ ...s, briefing_send_hour: parseInt(e.target.value) })}
                data-testid="fb-briefing-hour"
                className="px-2 py-1 border border-[#E5E5E0] rounded text-sm bg-white"
              >
                {Array.from({ length: 24 }, (_, i) => i).map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#5C5C59] block mb-1">Empfänger-E-Mail (leer = Admin-Benachrichtigungs-Adresse)</label>
              <input
                type="email"
                value={s.briefing_email || ''}
                onChange={e => setS({ ...s, briefing_email: e.target.value })}
                placeholder="team@restaurant.de"
                data-testid="fb-briefing-email"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
              />
            </div>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-[#E5E5E0] flex flex-wrap gap-2">
        <button onClick={save} disabled={busy} data-testid="fb-settings-save" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-6 py-2.5 rounded text-sm">
          {busy ? 'Speichere...' : 'Speichern'}
        </button>
        <button onClick={runNow} disabled={runBusy} data-testid="fb-run-dispatcher" className="border border-[#E5E5E0] hover:border-[#1C1C19] text-[#1C1C19] px-6 py-2.5 rounded text-sm">
          {runBusy ? 'Läuft...' : 'Dispatcher jetzt ausführen'}
        </button>
      </div>
    </div>
  );
}



const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function SmartTableTab() {
  const [s, setS] = useState(null);
  const [areas, setAreas] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/settings'), api.get('/areas')])
      .then(([r1, r2]) => { setS(r1.data); setAreas(r2.data); })
      .catch(() => toast.error('Fehler beim Laden'));
  }, []);

  if (!s) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div></div>;

  const groups = s.buffer_by_group_size || [];
  const peaks = s.peak_times || [];

  const addGroup = () => setS({ ...s, buffer_by_group_size: [...groups, { min: 1, max: 4, buffer: 10 }] });
  const updateGroup = (i, key, val) => {
    const next = [...groups];
    next[i] = { ...next[i], [key]: parseInt(val) || 0 };
    setS({ ...s, buffer_by_group_size: next });
  };
  const removeGroup = (i) => setS({ ...s, buffer_by_group_size: groups.filter((_, idx) => idx !== i) });

  const addPeak = () => setS({ ...s, peak_times: [...peaks, {
    weekday: 5, start_time: '18:00', end_time: '21:00',
    max_duration_minutes: 0, require_manual_confirm: false, allowed_area_ids: [],
  }] });
  const updatePeak = (i, key, val) => {
    const next = [...peaks];
    next[i] = { ...next[i], [key]: val };
    setS({ ...s, peak_times: next });
  };
  const removePeak = (i) => setS({ ...s, peak_times: peaks.filter((_, idx) => idx !== i) });
  const togglePeakArea = (i, areaId) => {
    const cur = peaks[i].allowed_area_ids || [];
    const next = cur.includes(areaId) ? cur.filter(x => x !== areaId) : [...cur, areaId];
    updatePeak(i, 'allowed_area_ids', next);
  };

  const slotLimits = s.peak_slot_limits || [];
  const addSlotLimit = () => setS({ ...s, peak_slot_limits: [...slotLimits, { start_time: '18:00', end_time: '19:30', max_guests: 20 }] });
  const updateSlotLimit = (i, key, val) => {
    const next = [...slotLimits];
    next[i] = { ...next[i], [key]: val };
    setS({ ...s, peak_slot_limits: next });
  };
  const removeSlotLimit = (i) => setS({ ...s, peak_slot_limits: slotLimits.filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings', {
        auto_release_cleaning_minutes: parseInt(s.auto_release_cleaning_minutes) || 0,
        auto_complete_enabled: !!s.auto_complete_enabled,
        auto_complete_grace_minutes: parseInt(s.auto_complete_grace_minutes) || 30,
        auto_release_from_seating: !!s.auto_release_from_seating,
        auto_no_show_enabled: !!s.auto_no_show_enabled,
        auto_no_show_after_minutes: parseInt(s.auto_no_show_after_minutes) || 30,
        buffer_by_group_size: (s.buffer_by_group_size || []).map(g => ({
          min: parseInt(g.min) || 1,
          max: parseInt(g.max) || 1,
          buffer: parseInt(g.buffer) || 0,
        })),
        peak_times: (s.peak_times || []).map(p => ({
          weekday: parseInt(p.weekday),
          start_time: p.start_time,
          end_time: p.end_time,
          max_duration_minutes: parseInt(p.max_duration_minutes) || 0,
          require_manual_confirm: !!p.require_manual_confirm,
          allowed_area_ids: p.allowed_area_ids || [],
        })),
        peak_slot_limits: (s.peak_slot_limits || [])
          .filter(l => l.start_time && l.end_time)
          .map(l => ({
            start_time: l.start_time,
            end_time: l.end_time,
            max_guests: parseInt(l.max_guests) || 0,
          })),
      });
      toast.success('Gespeichert');
    } catch { toast.error('Fehler'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-6 max-w-3xl" data-testid="smart-tab">
      <div>
        <h2 className="font-display text-xl mb-1">Intelligente Tischvergabe</h2>
        <p className="text-sm text-[#5C5C59]">
          Pufferzeiten, Stoßzeiten und automatische Reinigungsfreigabe. Die Grundregeln (Kapazität, Priorität, Bereich, Sperren) gelten immer und sind hier nicht änderbar.
        </p>
      </div>

      <section data-testid="smart-cleaning">
        <h3 className="font-display text-base mb-2">Reinigung automatisch freigeben</h3>
        <p className="text-xs text-[#5C5C59] mb-2">
          Tische im Status „Reinigung" werden nach X Minuten automatisch wieder freigeschaltet. <strong>0 = nur manuell.</strong>
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" max="1440"
            value={s.auto_release_cleaning_minutes ?? 0}
            onChange={e => setS({ ...s, auto_release_cleaning_minutes: parseInt(e.target.value) || 0 })}
            data-testid="auto-release-minutes"
            className="w-32 px-3 py-2 border border-[#E5E5E0] rounded text-sm"
          />
          <span className="text-sm text-[#5C5C59]">Minuten</span>
        </div>
      </section>

      <section className="pt-4 border-t border-[#E5E5E0]" data-testid="smart-auto-status">
        <h3 className="font-display text-base mb-3">Automatischer Status</h3>

        {/* Auto-Abschluss */}
        <div className="bg-[#F9F9F7] rounded-lg p-4 border border-[#E5E5E0] mb-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.auto_complete_enabled}
              onChange={e => setS({ ...s, auto_complete_enabled: e.target.checked })}
              data-testid="auto-complete-enabled"
              className="mt-1 w-4 h-4 accent-[#1C1C19]"
            />
            <span>
              <span className="font-medium text-sm text-[#1C1C19] block">Reservierungen automatisch abschließen</span>
              <span className="text-xs text-[#5C5C59]">
                Reservierungen (bestätigt/angekommen/platziert) werden nach Ende der Reservierung automatisch auf „abgeschlossen" gesetzt. Das löst auch die <strong>Feedback-Mail</strong> aus (nur Reservierungen der letzten 2 Tage).
              </span>
            </span>
          </label>
          {s.auto_complete_enabled && (
            <div className="flex items-center gap-2 mt-3 ml-7">
              <span className="text-xs text-[#5C5C59]">Abschließen</span>
              <input
                type="number" min="0" max="240"
                value={s.auto_complete_grace_minutes ?? 30}
                onChange={e => setS({ ...s, auto_complete_grace_minutes: parseInt(e.target.value) || 0 })}
                data-testid="auto-complete-minutes"
                className="w-20 px-2 py-1.5 border border-[#E5E5E0] rounded text-sm"
              />
              <span className="text-xs text-[#5C5C59]">Min. nach {s.auto_release_from_seating ? 'Ende der Aufenthaltsdauer' : 'Reservierungs-Ende'}</span>
            </div>
          )}
          {s.auto_complete_enabled && (
            <label className="flex items-start gap-3 mt-3 ml-7 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.auto_release_from_seating}
                onChange={e => setS({ ...s, auto_release_from_seating: e.target.checked })}
                data-testid="auto-release-from-seating"
                className="mt-0.5 w-4 h-4 accent-[#1C1C19]"
              />
              <span>
                <span className="font-medium text-xs text-[#1C1C19] block">Freigabe ab tatsächlichem Hinsetzen berechnen</span>
                <span className="text-xs text-[#5C5C59]">
                  Der Timer startet, sobald der Gast als „platziert" markiert wird (Hinsetz-Zeit + gebuchte Dauer), statt ab der gebuchten Endzeit. Ohne Hinsetz-Zeit gilt die gebuchte Endzeit.
                </span>
              </span>
            </label>
          )}
        </div>

        {/* Auto No-Show */}
        <div className="bg-[#F9F9F7] rounded-lg p-4 border border-[#E5E5E0]">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.auto_no_show_enabled}
              onChange={e => setS({ ...s, auto_no_show_enabled: e.target.checked })}
              data-testid="auto-no-show-enabled"
              className="mt-1 w-4 h-4 accent-[#1C1C19]"
            />
            <span>
              <span className="font-medium text-sm text-[#1C1C19] block">No-Show automatisch markieren</span>
              <span className="text-xs text-[#5C5C59]">
                Reservierungen, die nicht als „angekommen/platziert" markiert wurden, werden X Min. nach Beginn automatisch als <strong>No-Show</strong> gesetzt und der Tisch wieder freigegeben.
                <strong className="text-[#B45309]"> Nur aktivieren, wenn ihr Gäste aktiv als „angekommen" markiert!</strong>
              </span>
            </span>
          </label>
          {s.auto_no_show_enabled && (
            <div className="flex items-center gap-2 mt-3 ml-7">
              <span className="text-xs text-[#5C5C59]">No-Show</span>
              <input
                type="number" min="5" max="240"
                value={s.auto_no_show_after_minutes ?? 30}
                onChange={e => setS({ ...s, auto_no_show_after_minutes: parseInt(e.target.value) || 0 })}
                data-testid="auto-no-show-minutes"
                className="w-20 px-2 py-1.5 border border-[#E5E5E0] rounded text-sm"
              />
              <span className="text-xs text-[#5C5C59]">Min. nach Reservierungs-Beginn</span>
            </div>
          )}
        </div>
      </section>

      <section className="pt-4 border-t border-[#E5E5E0]" data-testid="smart-buffer-groups">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-display text-base">Pufferzeit nach Gruppengröße</h3>
            <p className="text-xs text-[#5C5C59]">Setzt die Standard-Pufferzeit für bestimmte Personenzahlen außer Kraft.</p>
          </div>
          <button onClick={addGroup} data-testid="add-buffer-group" className="text-sm border border-[#E5E5E0] hover:border-[#1C1C19] rounded px-3 py-1.5">+ Regel</button>
        </div>
        {groups.length === 0 && <p className="text-xs text-[#9CA3AF] italic">Keine Regeln — Standard-Puffer aus „Allgemein" wird verwendet.</p>}
        <div className="space-y-2">
          {groups.map((g, i) => (
            <div key={i} className="flex items-center gap-2 bg-[#F9F9F7] rounded p-2 border border-[#E5E5E0]" data-testid={`buffer-group-${i}`}>
              <span className="text-xs text-[#5C5C59]">Von</span>
              <input type="number" min="1" max="99" value={g.min} onChange={e => updateGroup(i, 'min', e.target.value)} data-testid={`buffer-group-${i}-min`} className="w-16 px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-xs text-[#5C5C59]">bis</span>
              <input type="number" min="1" max="99" value={g.max} onChange={e => updateGroup(i, 'max', e.target.value)} data-testid={`buffer-group-${i}-max`} className="w-16 px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-xs text-[#5C5C59]">Pers. =</span>
              <input type="number" min="0" max="120" value={g.buffer} onChange={e => updateGroup(i, 'buffer', e.target.value)} data-testid={`buffer-group-${i}-buffer`} className="w-20 px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-xs text-[#5C5C59]">Min Puffer</span>
              <button onClick={() => removeGroup(i)} data-testid={`buffer-group-${i}-del`} className="ml-auto text-[#EF4444] hover:bg-[#FEE2E2] p-1 rounded">×</button>
            </div>
          ))}
        </div>
      </section>

      <section className="pt-4 border-t border-[#E5E5E0]" data-testid="smart-peaks">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-display text-base">Stoßzeiten</h3>
            <p className="text-xs text-[#5C5C59]">Pro Wochentag: Zeitfenster mit max. Aufenthalt + manueller Bestätigung + Bereichs-Beschränkung.</p>
          </div>
          <button onClick={addPeak} data-testid="add-peak" className="text-sm border border-[#E5E5E0] hover:border-[#1C1C19] rounded px-3 py-1.5">+ Stoßzeit</button>
        </div>
        {peaks.length === 0 && <p className="text-xs text-[#9CA3AF] italic">Keine Stoßzeiten definiert.</p>}
        <div className="space-y-3">
          {peaks.map((p, i) => (
            <div key={i} className="bg-[#F9F9F7] rounded p-3 border border-[#E5E5E0] space-y-2" data-testid={`peak-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={p.weekday ?? 5} onChange={e => updatePeak(i, 'weekday', parseInt(e.target.value))} data-testid={`peak-${i}-weekday`} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm bg-white">
                  {WEEKDAYS_DE.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
                </select>
                <input type="time" value={p.start_time} onChange={e => updatePeak(i, 'start_time', e.target.value)} data-testid={`peak-${i}-start`} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
                <span className="text-xs text-[#5C5C59]">bis</span>
                <input type="time" value={p.end_time} onChange={e => updatePeak(i, 'end_time', e.target.value)} data-testid={`peak-${i}-end`} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
                <span className="text-xs text-[#5C5C59]">Max-Dauer:</span>
                <input type="number" min="0" max="600" value={p.max_duration_minutes ?? 0} onChange={e => updatePeak(i, 'max_duration_minutes', parseInt(e.target.value) || 0)} data-testid={`peak-${i}-maxdur`} className="w-20 px-2 py-1 border border-[#E5E5E0] rounded text-sm" placeholder="0 = aus" />
                <span className="text-xs text-[#5C5C59]">Min</span>
                <button onClick={() => removePeak(i)} data-testid={`peak-${i}-del`} className="ml-auto text-[#EF4444] hover:bg-[#FEE2E2] p-1 rounded">×</button>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={p.require_manual_confirm ?? false} onChange={e => updatePeak(i, 'require_manual_confirm', e.target.checked)} data-testid={`peak-${i}-manual`} />
                Online-Reservierungen erfordern manuelle Bestätigung
              </label>
              {areas.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-1">Erlaubte Bereiche (leer = alle)</p>
                  <div className="flex flex-wrap gap-1">
                    {areas.map(a => {
                      const sel = (p.allowed_area_ids || []).includes(a.id);
                      return (
                        <button key={a.id} onClick={() => togglePeakArea(i, a.id)} data-testid={`peak-${i}-area-${a.id}`} className={`text-xs px-2 py-1 rounded-full border ${sel ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}>{a.name}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="pt-4 border-t border-[#E5E5E0]" data-testid="smart-slot-limits">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-display text-base">Gäste-Limit pro Uhrzeit (Stoßzeiten-Drosselung)</h3>
            <p className="text-xs text-[#5C5C59] max-w-xl">
              Begrenzt die Anzahl <strong>Online-Gäste pro exakter Uhrzeit</strong>. Ist das Limit erreicht,
              verschwindet nur dieser Slot aus dem Buchungsformular – benachbarte Zeiten (z. B. 17:45, 18:15) bleiben buchbar.
              Es zählen nur Online-Reservierungen; das Limit gilt für alle Buchungskanäle (Walk-ins ausgenommen).
            </p>
          </div>
          <button onClick={addSlotLimit} data-testid="add-slot-limit" className="text-sm border border-[#E5E5E0] hover:border-[#1C1C19] rounded px-3 py-1.5 shrink-0">+ Limit</button>
        </div>
        {slotLimits.length === 0 && <p className="text-xs text-[#9CA3AF] italic">Kein Limit definiert — alle Uhrzeiten unbegrenzt (nur durch freie Tische limitiert).</p>}
        <div className="space-y-2">
          {slotLimits.map((l, i) => (
            <div key={i} className="flex items-center gap-2 bg-[#F9F9F7] rounded p-2 border border-[#E5E5E0] flex-wrap" data-testid={`slot-limit-${i}`}>
              <span className="text-xs text-[#5C5C59]">Zeitraum</span>
              <input type="time" value={l.start_time} onChange={e => updateSlotLimit(i, 'start_time', e.target.value)} data-testid={`slot-limit-${i}-start`} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-xs text-[#5C5C59]">bis</span>
              <input type="time" value={l.end_time} onChange={e => updateSlotLimit(i, 'end_time', e.target.value)} data-testid={`slot-limit-${i}-end`} className="px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-xs text-[#5C5C59]">= max.</span>
              <input type="number" min="0" max="999" value={l.max_guests} onChange={e => updateSlotLimit(i, 'max_guests', e.target.value)} data-testid={`slot-limit-${i}-max`} className="w-20 px-2 py-1 border border-[#E5E5E0] rounded text-sm" />
              <span className="text-xs text-[#5C5C59]">Gäste pro Uhrzeit</span>
              <button onClick={() => removeSlotLimit(i)} data-testid={`slot-limit-${i}-del`} className="ml-auto text-[#EF4444] hover:bg-[#FEE2E2] p-1 rounded">×</button>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-4 border-t border-[#E5E5E0]">
        <button onClick={save} disabled={busy} data-testid="smart-save" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-6 py-2.5 rounded text-sm">
          {busy ? 'Speichere...' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Widget tab — generates copy-paste snippets to embed the reservation form on
// the restaurant's own website.
// ---------------------------------------------------------------------------
function WidgetTab() {
  const origin = window.location.origin;
  const embedUrl = `${origin}/embed`;
  const [copied, setCopied] = useState('');

  const copy = (label, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(''), 1800);
      toast.success('In Zwischenablage kopiert');
    }).catch(() => toast.error('Konnte nicht kopieren'));
  };

  const iframeSnippet = `<iframe
  src="${embedUrl}"
  title="Tischreservierung"
  style="width:100%;min-height:780px;border:0;display:block;"
  allow="clipboard-write"
></iframe>
<script>
  // Optional: auto-resize the iframe to its content height
  window.addEventListener('message', function (e) {
    if (e.data && e.data.type === 'lacontadina:resize') {
      var f = document.querySelector('iframe[src="${embedUrl}"]');
      if (f) f.style.height = e.data.height + 'px';
    }
  });
</script>`;

  const buttonSnippet = `<!-- "Reservieren" button + modal popup -->
<button id="lc-book-btn" style="background:#1C1C19;color:#fff;padding:12px 24px;border:0;border-radius:6px;font-family:inherit;font-size:14px;letter-spacing:0.05em;cursor:pointer;">
  Tisch reservieren
</button>
<div id="lc-book-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:9999;align-items:center;justify-content:center;padding:16px;">
  <div style="background:#fff;border-radius:12px;width:100%;max-width:760px;max-height:92vh;overflow:hidden;position:relative;">
    <button id="lc-book-close" aria-label="Schließen" style="position:absolute;top:8px;right:8px;z-index:2;width:36px;height:36px;border:0;background:#fff;border-radius:50%;font-size:20px;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,0.15);">×</button>
    <iframe id="lc-book-iframe" src="${embedUrl}" style="width:100%;height:80vh;border:0;display:block;" title="Tischreservierung"></iframe>
  </div>
</div>
<script>
  (function () {
    var btn = document.getElementById('lc-book-btn');
    var modal = document.getElementById('lc-book-modal');
    var close = document.getElementById('lc-book-close');
    btn.addEventListener('click', function () { modal.style.display = 'flex'; document.body.style.overflow = 'hidden'; });
    close.addEventListener('click', function () { modal.style.display = 'none'; document.body.style.overflow = ''; });
    modal.addEventListener('click', function (e) { if (e.target === modal) close.click(); });
    window.addEventListener('message', function (e) {
      if (e.data && e.data.type === 'lacontadina:reservation-created') {
        // Optional: send analytics, auto-close after a delay etc.
        setTimeout(function () { /* close.click(); */ }, 8000);
      }
    });
  })();
</script>`;

  return (
    <div className="space-y-6" data-testid="widget-tab">
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-display text-lg text-[#1C1C19] mb-1">Reservierungs-Widget</h3>
        <p className="text-sm text-[#5C5C59]">
          Bauen Sie das Reservierungsformular direkt in Ihre eigene Website (z. B. WordPress, Wix, Squarespace) ein.
          Wählen Sie eine der drei Varianten unten.
        </p>
      </div>

      {/* Direct URL */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h4 className="font-medium text-sm text-[#1C1C19] mb-2">1. Direkt-Link (für „Reservieren"-Button)</h4>
        <p className="text-xs text-[#5C5C59] mb-3">
          Verlinken Sie diesen URL z. B. von Ihrer Navigation oder einem Button. Öffnet das ganze Formular in einer eigenen Seite.
        </p>
        <div className="flex gap-2 items-center">
          <input
            readOnly
            value={embedUrl}
            data-testid="widget-embed-url"
            className="flex-1 font-mono text-xs px-3 py-2 border border-[#E5E5E0] rounded bg-[#F9F9F7]"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={() => copy('url', embedUrl)}
            data-testid="widget-copy-url"
            className="px-4 py-2 text-xs bg-[#1C1C19] text-white rounded hover:bg-[#3A3A35]"
          >
            {copied === 'url' ? '✓ Kopiert' : 'Kopieren'}
          </button>
          <a
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="widget-preview-url"
            className="px-4 py-2 text-xs border border-[#1C1C19] text-[#1C1C19] rounded hover:bg-[#1C1C19] hover:text-white transition"
          >
            Vorschau
          </a>
        </div>
      </div>

      {/* Google Maps / Business Profile */}
      <div className="bg-white border-2 border-[#4285F4]/40 rounded-lg p-5" data-testid="widget-google-section">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#4285F4]/10 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"/>
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-sm text-[#1C1C19]">2. „Tisch reservieren" bei Google Maps anzeigen</h4>
            <p className="text-xs text-[#5C5C59] mt-1">
              Damit Gäste direkt aus Google Maps / der Google-Suche reservieren können, hinterlegen Sie diese URL in Ihrem Google Business Profile als „Termin-URL" / „Reservierungslink".
            </p>
          </div>
        </div>

        {/* Production-URL: prefer the user's custom domain if we're already on it,
            otherwise use the same origin. */}
        <div className="bg-[#F9F9F7] rounded p-3 mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-1">Diese URL bei Google eintragen:</div>
          <div className="flex gap-2 items-center">
            <input
              readOnly
              value={origin}
              data-testid="widget-google-url"
              className="flex-1 font-mono text-xs px-3 py-2 border border-[#E5E5E0] rounded bg-white"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={() => copy('google', origin)}
              data-testid="widget-copy-google"
              className="px-4 py-2 text-xs bg-[#4285F4] text-white rounded hover:bg-[#3367D6]"
            >
              {copied === 'google' ? '✓ Kopiert' : 'URL kopieren'}
            </button>
          </div>
          <p className="text-[10px] text-[#5C5C59] mt-2">
            💡 Tipp: Nutzen Sie die <b>Hauptdomain</b> (Startseite), damit Google die Seite mit Logo + Bild + Marketing-Text anzeigt – nicht die <code>/embed</code>-Version.
          </p>
        </div>

        {/* Step-by-step */}
        <div className="border-t border-[#E5E5E0] pt-3">
          <div className="text-[10px] uppercase tracking-wider text-[#5C5C59] mb-2 font-medium">So geht's:</div>
          <ol className="text-xs text-[#5C5C59] space-y-2 list-decimal list-inside">
            <li>
              Gehen Sie zu <a href="https://business.google.com/" target="_blank" rel="noopener noreferrer" className="text-[#4285F4] underline">business.google.com</a> und melden Sie sich mit dem Inhaber-Konto an.
            </li>
            <li>Wählen Sie Ihr Restaurant aus.</li>
            <li>Klicken Sie auf <b>„Profil bearbeiten"</b> oder <b>„Mehr"</b> → <b>„Bestellungen / Reservierungen"</b>.</li>
            <li>
              Suchen Sie die Felder <b>„Termin-URL"</b>, <b>„Reservierungslink"</b> oder <b>„URL für Reservierungen"</b>.
            </li>
            <li>
              Fügen Sie die kopierte URL oben ein und speichern Sie.
            </li>
            <li>
              Es kann ein paar Stunden bis 3 Tage dauern, bis Google die Änderung freischaltet und einen <b>„Tisch reservieren"</b>-Button in Ihrem Profil + bei Google Maps anzeigt.
            </li>
          </ol>
        </div>

        <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded p-3 mt-3 text-xs text-[#92400E]">
          <b>Hinweis:</b> Falls Google bei Ihrem Restaurant bereits einen Reservierungs-Partner (TheFork, OpenTable, Quandoo) anzeigt, müssen Sie diesen ggf. zuerst trennen oder Google nach einer Änderung des Standard-Anbieters fragen, damit Ihre eigene URL prominent erscheint.
        </div>
      </div>

      {/* iframe snippet */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h4 className="font-medium text-sm text-[#1C1C19]">3. iframe einbetten</h4>
            <p className="text-xs text-[#5C5C59] mt-1">
              Kopieren Sie diesen HTML-Code und fügen Sie ihn in den Quelltext Ihrer Website ein, wo das Formular erscheinen soll.
              Das Skript am Ende passt die Höhe automatisch an.
            </p>
          </div>
          <button
            onClick={() => copy('iframe', iframeSnippet)}
            data-testid="widget-copy-iframe"
            className="shrink-0 px-4 py-2 text-xs bg-[#1C1C19] text-white rounded hover:bg-[#3A3A35]"
          >
            {copied === 'iframe' ? '✓ Kopiert' : 'Code kopieren'}
          </button>
        </div>
        <pre className="bg-[#1C1C19] text-[#E5E5E0] p-3 rounded text-[11px] font-mono overflow-x-auto whitespace-pre" data-testid="widget-iframe-code">
{iframeSnippet}
        </pre>
      </div>

      {/* Floating button + modal snippet */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <h4 className="font-medium text-sm text-[#1C1C19]">4. Reservieren-Button + Popup</h4>
            <p className="text-xs text-[#5C5C59] mt-1">
              Setzt einen schwarzen „Tisch reservieren"-Button in Ihre Seite. Klick öffnet das Formular in einem Popup-Fenster.
              Passt sich responsive an Handy/Desktop an.
            </p>
          </div>
          <button
            onClick={() => copy('button', buttonSnippet)}
            data-testid="widget-copy-button"
            className="shrink-0 px-4 py-2 text-xs bg-[#1C1C19] text-white rounded hover:bg-[#3A3A35]"
          >
            {copied === 'button' ? '✓ Kopiert' : 'Code kopieren'}
          </button>
        </div>
        <pre className="bg-[#1C1C19] text-[#E5E5E0] p-3 rounded text-[11px] font-mono overflow-x-auto whitespace-pre max-h-72" data-testid="widget-button-code">
{buttonSnippet}
        </pre>
      </div>

      {/* Tips */}
      <div className="bg-[#F9F9F7] border border-[#E5E5E0] rounded-lg p-5">
        <h4 className="font-medium text-sm text-[#1C1C19] mb-2">💡 Hinweise</h4>
        <ul className="text-xs text-[#5C5C59] space-y-1.5 list-disc list-inside">
          <li>WordPress: nutzen Sie einen „Custom HTML"-Block oder das Plugin „Insert Headers and Footers".</li>
          <li>Wix / Squarespace: fügen Sie ein „Embed Code"-Element ein und kopieren Sie den Snippet hinein.</li>
          <li>Bei erfolgreicher Reservierung schickt das Widget eine <code className="px-1 py-0.5 bg-white rounded">postMessage</code> an Ihre Seite — können Sie für eigene Statistiken/Analytics auswerten.</li>
          <li>Das Widget übernimmt automatisch Logo, Bilder, Öffnungszeiten und Bereiche aus Ihren Einstellungen — keine separate Konfiguration nötig.</li>
        </ul>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// BlockedPeriodsTab — admin can block specific time windows on specific dates,
// e.g. "Block 18:00-19:30 on 2026-06-17 (private event)". Multiple blocks
// per day allowed.
// ---------------------------------------------------------------------------
function BlockedPeriodsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '', note: '' });
  const [bulkDays, setBulkDays] = useState([]); // for multi-date "bulk" mode
  const [bulkMode, setBulkMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const r = await api.get(`/blocked-periods?date_from=${today}`);
    setList(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.start_time || !form.end_time) {
      toast.error('Bitte Start- und Endzeit angeben');
      return;
    }
    if (form.end_time <= form.start_time) {
      toast.error('Endzeit muss nach der Startzeit liegen');
      return;
    }
    const dates = bulkMode
      ? bulkDays.filter(Boolean)
      : (form.date ? [form.date] : []);
    if (!dates.length) {
      toast.error('Bitte mindestens ein Datum wählen');
      return;
    }
    setSaving(true);
    try {
      const created = [];
      for (const d of dates) {
        const r = await api.post('/blocked-periods', { date: d, start_time: form.start_time, end_time: form.end_time, note: form.note || '' });
        created.push(r.data);
      }
      toast.success(`${created.length} Sperrzeit${created.length === 1 ? '' : 'en'} angelegt`);
      setForm({ date: '', start_time: '', end_time: '', note: '' });
      setBulkDays([]);
      setBulkMode(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const removeOne = async (id) => {
    if (!confirm('Sperrzeit wirklich entfernen?')) return;
    try {
      await api.delete(`/blocked-periods/${id}`);
      toast.success('Entfernt');
      await load();
    } catch {
      toast.error('Fehler beim Entfernen');
    }
  };

  // Group by date for nicer rendering
  const grouped = {};
  list.forEach(bp => { (grouped[bp.date] = grouped[bp.date] || []).push(bp); });

  return (
    <div className="space-y-6" data-testid="blocked-periods-tab">
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-display text-lg text-[#1C1C19] mb-1">Sperrzeiten</h3>
        <p className="text-sm text-[#5C5C59]">
          Blockieren Sie eine oder mehrere Uhrzeit-Fenster, in denen <b>keine</b> Online-Reservierungen angenommen werden.
          Beispiel: 18:00–19:30 für eine geschlossene Gesellschaft.
          Die Sperrzeit gilt unabhängig vom Tisch (alle Bereiche gesperrt für diese Zeit).
        </p>
      </div>

      {/* Form */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h4 className="font-medium text-sm text-[#1C1C19] mb-4">Neue Sperrzeit anlegen</h4>

        {/* Bulk toggle */}
        <div className="mb-4 flex items-center gap-3 text-xs">
          <button
            type="button"
            onClick={() => { setBulkMode(false); setBulkDays([]); }}
            data-testid="bp-mode-single"
            className={`px-3 py-1.5 rounded ${!bulkMode ? 'bg-[#1C1C19] text-white' : 'border border-[#E5E5E0] text-[#5C5C59]'}`}
          >Einzelner Tag</button>
          <button
            type="button"
            onClick={() => { setBulkMode(true); setForm(f => ({ ...f, date: '' })); }}
            data-testid="bp-mode-bulk"
            className={`px-3 py-1.5 rounded ${bulkMode ? 'bg-[#1C1C19] text-white' : 'border border-[#E5E5E0] text-[#5C5C59]'}`}
          >Mehrere Tage</button>
        </div>

        {!bulkMode ? (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Datum</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="bp-date"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Von</label>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="bp-start"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Bis</label>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                data-testid="bp-end"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Notiz (optional)</label>
              <input
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="z. B. Privat-Event"
                data-testid="bp-note"
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#5C5C59]">Wählen Sie mehrere Datümer (Strg/Cmd-Klick oder über das Plus hinzufügen):</p>
            <div className="flex flex-wrap gap-2">
              {bulkDays.map((d, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#F9F9F7] border border-[#E5E5E0] rounded font-mono">
                  {d}
                  <button type="button" onClick={() => setBulkDays(prev => prev.filter((_, idx) => idx !== i))} className="text-[#5C5C59] hover:text-[#EF4444]">×</button>
                </span>
              ))}
              <input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  if (e.target.value && !bulkDays.includes(e.target.value)) {
                    setBulkDays(prev => [...prev, e.target.value].sort());
                  }
                  e.target.value = '';
                }}
                data-testid="bp-bulk-add-date"
                className="px-2 py-1 border border-dashed border-[#1C1C19] rounded text-xs font-mono"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Von</label>
                <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Bis</label>
                <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5C5C59] block mb-1">Notiz</label>
                <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm" />
              </div>
            </div>
          </div>
        )}

        <button
          onClick={submit}
          disabled={saving}
          data-testid="bp-submit"
          className="mt-4 px-4 py-2 bg-[#1C1C19] text-white text-sm rounded hover:bg-[#3A3A35] disabled:opacity-50"
        >
          {saving ? 'Speichert...' : `Sperrzeit${bulkMode && bulkDays.length > 1 ? `en (${bulkDays.length})` : ''} anlegen`}
        </button>
      </div>

      {/* Existing list */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
          <h4 className="font-medium text-sm text-[#1C1C19]">Aktive Sperrzeiten ({list.length})</h4>
        </div>
        {list.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-[#5C5C59]" data-testid="bp-empty">
            Keine Sperrzeiten angelegt — alle Öffnungszeiten sind frei buchbar.
          </div>
        ) : (
          <div className="divide-y divide-[#E5E5E0]">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => (
              <div key={date} className="px-5 py-3" data-testid={`bp-date-group-${date}`}>
                <div className="text-xs uppercase tracking-wider text-[#5C5C59] mb-2 font-mono">{date}</div>
                <div className="space-y-2">
                  {items.sort((a, b) => a.start_time.localeCompare(b.start_time)).map(bp => (
                    <div key={bp.id} data-testid={`bp-row-${bp.id}`} className="flex items-center gap-3 text-sm bg-[#F9F9F7] rounded px-3 py-2">
                      <span className="font-mono text-[#EF4444] font-medium">{bp.start_time} – {bp.end_time}</span>
                      {bp.note && <span className="text-[#5C5C59] italic flex-1">"{bp.note}"</span>}
                      {!bp.note && <span className="flex-1" />}
                      <button
                        onClick={() => removeOne(bp.id)}
                        data-testid={`bp-delete-${bp.id}`}
                        className="text-xs text-[#EF4444] hover:underline"
                      >Entfernen</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

